import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import axios from "axios";
import { toast } from "react-toastify";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import "./StoreControl.css";
import { buildTreeFromFlat, flattenTree } from "../../../utils/categoryTreeUtils";
import {
  clientPointToMediaPercent,
  mediaPercentToContainerCss,
} from "../../../utils/hotspotCoords";
import { resolveUrl } from "../../../utils/storeUtils";

const TILE_PREVIEW_ASPECT_W = 16;
const TILE_PREVIEW_ASPECT_H = 9;

function normalizeHeroOrder(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function normalizeHeroImageIndex(value, fallback = 0) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(0, Math.floor(parsed));
}

function normalizeHeroImageFit(value, fallback = "cover") {
  const normalized = String(value || "").trim().toLowerCase();
  return ["cover", "contain"].includes(normalized) ? normalized : fallback;
}

function clampPercent(value, fallback = 50) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(0, Math.min(100, parsed));
}

function normalizePercent(value, fallback = 50) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(100, Math.max(0, parsed));
}

function formatHotspotCoord(value, fallback = 50) {
  const n = normalizePercent(value, fallback);
  return Math.round(n * 10) / 10;
}

function normalizeId(value) {
  if (!value) return "";
  if (typeof value === "object" && value._id) return String(value._id);
  return String(value);
}

const DEFAULT_TRUST_ITEMS = [
  { en: "", ar: "", icon: "fa-award" },
  { en: "", ar: "", icon: "fa-layer-group" },
  { en: "", ar: "", icon: "fa-headset" },
];

function normalizeTrustItems(val) {
  const arr = Array.isArray(val) ? val : [];
  const result = [0, 1, 2].map((i) => ({
    en: String(arr[i]?.en ?? "").trim(),
    ar: String(arr[i]?.ar ?? "").trim(),
    icon: String(arr[i]?.icon ?? DEFAULT_TRUST_ITEMS[i].icon).trim(),
  }));
  return result;
}

function normalizeHeroProductItemForm(item) {
  if (!item) return null;
  const pid = String(item.productId || "").trim();
  if (!pid) return null;
  const n = (v) => Number(v ?? 0) || 0;
  const s = (v) => Math.max(0.1, Math.min(3, Number(v ?? 1) || 1));
  return {
    productId: pid,
    imageUrl: String(item.imageUrl || "").trim(),
    x: n(item.x), y: n(item.y), scale: s(item.scale),
    xAr: n(item.xAr), yAr: n(item.yAr), scaleAr: s(item.scaleAr),
    mobileX: n(item.mobileX), mobileY: n(item.mobileY), mobileScale: s(item.mobileScale),
    mobileXAr: n(item.mobileXAr), mobileYAr: n(item.mobileYAr), mobileScaleAr: s(item.mobileScaleAr),
  };
}

/** Supports legacy flat string or { desktop, mobile, productImage, heroProductId } from admin form */
function normalizeHeroSlideFormValue(val) {
  if (val == null) return {
    desktop: "", mobile: "", desktopAr: "", mobileAr: "", productImage: "", heroProductId: "",
    heroProductItems: [],
    heroProductX: 0, heroProductY: 0, heroProductScale: 1,
    heroBtnX: 0, heroBtnY: 0, heroBtnMobileX: 0, heroBtnMobileY: 0,
    heroProductMobileX: 0, heroProductMobileY: 0, heroProductMobileScale: 1,
    heroTextX: 0, heroTextY: 0,
    heroProductXAr: 0, heroProductYAr: 0, heroProductScaleAr: 1,
    heroTextXAr: 0, heroTextYAr: 0,
    heroBtnXAr: 0, heroBtnYAr: 0,
    heroProductMobileXAr: 0, heroProductMobileYAr: 0, heroProductMobileScaleAr: 1,
    heroBtnMobileXAr: 0, heroBtnMobileYAr: 0,
    heroTitleEn: "", heroTitleAr: "", heroSubtitleEn: "", heroSubtitleAr: "",
    heroTrustItems: DEFAULT_TRUST_ITEMS,
    heroDotsX: 0, heroDotsY: 16,
    heroDotsMobileX: 0, heroDotsMobileY: 10,
  };
  if (typeof val === "string") return {
    desktop: String(val).trim(), mobile: "", desktopAr: "", mobileAr: "", productImage: "", heroProductId: "",
    heroProductItems: [],
    heroProductX: 0, heroProductY: 0, heroProductScale: 1,
    heroBtnX: 0, heroBtnY: 0, heroBtnMobileX: 0, heroBtnMobileY: 0,
    heroProductMobileX: 0, heroProductMobileY: 0, heroProductMobileScale: 1,
    heroTextX: 0, heroTextY: 0,
    heroProductXAr: 0, heroProductYAr: 0, heroProductScaleAr: 1,
    heroTextXAr: 0, heroTextYAr: 0,
    heroBtnXAr: 0, heroBtnYAr: 0,
    heroProductMobileXAr: 0, heroProductMobileYAr: 0, heroProductMobileScaleAr: 1,
    heroBtnMobileXAr: 0, heroBtnMobileYAr: 0,
    heroTitleEn: "", heroTitleAr: "", heroSubtitleEn: "", heroSubtitleAr: "",
    heroTrustItems: DEFAULT_TRUST_ITEMS,
    heroDotsX: 0, heroDotsY: 16,
    heroDotsMobileX: 0, heroDotsMobileY: 10,
  };
  return {
    desktop: String(val.desktop ?? "").trim(),
    mobile: String(val.mobile ?? "").trim(),
    desktopAr: String(val.desktopAr ?? "").trim(),
    mobileAr: String(val.mobileAr ?? "").trim(),
    heroProductXAr: Number(val.heroProductXAr ?? 0) || 0,
    heroProductYAr: Number(val.heroProductYAr ?? 0) || 0,
    heroProductScaleAr: Math.max(0.1, Math.min(3, Number(val.heroProductScaleAr ?? 1) || 1)),
    heroTextXAr: Number(val.heroTextXAr ?? 0) || 0,
    heroTextYAr: Number(val.heroTextYAr ?? 0) || 0,
    heroBtnXAr: Number(val.heroBtnXAr ?? 0) || 0,
    heroBtnYAr: Number(val.heroBtnYAr ?? 0) || 0,
    heroProductMobileXAr: Number(val.heroProductMobileXAr ?? 0) || 0,
    heroProductMobileYAr: Number(val.heroProductMobileYAr ?? 0) || 0,
    heroProductMobileScaleAr: Math.max(0.1, Math.min(3, Number(val.heroProductMobileScaleAr ?? 1) || 1)),
    heroBtnMobileXAr: Number(val.heroBtnMobileXAr ?? 0) || 0,
    heroBtnMobileYAr: Number(val.heroBtnMobileYAr ?? 0) || 0,
    productImage: String(val.productImage ?? "").trim(),
    heroProductId: String(val.heroProductId ?? "").trim(),
    heroProductItems: Array.isArray(val.heroProductItems)
      ? val.heroProductItems.map(normalizeHeroProductItemForm).filter(Boolean)
      : [],
    heroProductX: Number(val.heroProductX ?? 0) || 0,
    heroProductY: Number(val.heroProductY ?? 0) || 0,
    heroProductScale: Math.max(0.1, Math.min(3, Number(val.heroProductScale ?? 1) || 1)),
    heroBtnX: Number(val.heroBtnX ?? 0) || 0,
    heroBtnY: Number(val.heroBtnY ?? 0) || 0,
    heroBtnMobileX: Number(val.heroBtnMobileX ?? 0) || 0,
    heroBtnMobileY: Number(val.heroBtnMobileY ?? 0) || 0,
    heroProductMobileX: Number(val.heroProductMobileX ?? 0) || 0,
    heroProductMobileY: Number(val.heroProductMobileY ?? 0) || 0,
    heroProductMobileScale: Math.max(0.1, Math.min(3, Number(val.heroProductMobileScale ?? 1) || 1)),
    heroTextX: Number(val.heroTextX ?? 0) || 0,
    heroTextY: Number(val.heroTextY ?? 0) || 0,
    heroTitleEn: String(val.heroTitleEn ?? ""),
    heroTitleAr: String(val.heroTitleAr ?? ""),
    heroSubtitleEn: String(val.heroSubtitleEn ?? ""),
    heroSubtitleAr: String(val.heroSubtitleAr ?? ""),
    heroTrustItems: normalizeTrustItems(val.heroTrustItems),
    heroDotsX: Number(val.heroDotsX ?? 0) || 0,
    heroDotsY: Math.max(0, Math.min(200, Number(val.heroDotsY ?? 16) || 0)),
    heroDotsMobileX: Number(val.heroDotsMobileX ?? 0) || 0,
    heroDotsMobileY: Math.max(0, Math.min(200, Number(val.heroDotsMobileY ?? 10) || 0)),
  };
}

function pickProductThumbnail(item, preferredIndex = 0) {
  const arr = Array.isArray(item?.images) ? item.images : [];
  const safeIndex = Math.min(
    Math.max(0, normalizeHeroImageIndex(preferredIndex, 0)),
    Math.max(arr.length - 1, 0)
  );
  const selected = arr[safeIndex] || arr[0];
  const fromImages = selected?.url ? String(selected.url).trim() : "";
  const legacy = item?.image?.url ? String(item.image.url).trim() : "";
  return fromImages || legacy || "";
}

function newHotspotLocalId() {
  return `hs-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
}

function parseHotspotsFromApiEntry(entry) {
  if (Array.isArray(entry?.hotspots) && entry.hotspots.length > 0) {
    return entry.hotspots.map((h) => ({
      localId: newHotspotLocalId(),
      hotspotX: normalizePercent(h?.x, 50),
      hotspotY: normalizePercent(h?.y, 50),
      productId: normalizeId(h?.productId),
    }));
  }
  return [
    {
      localId: newHotspotLocalId(),
      hotspotX: normalizePercent(entry?.hotspot?.x, 50),
      hotspotY: normalizePercent(entry?.hotspot?.y, 50),
      productId: normalizeId(entry?.hotspot?.productId),
    },
  ];
}

function tileMediaStateFromApiEntry(entry) {
  return {
    mediaType: ["default", "image", "video"].includes(String(entry?.mediaType || "").toLowerCase())
      ? String(entry.mediaType).toLowerCase()
      : "default",
    mediaUrl: String(entry?.mediaUrl || entry?.imageUrl || "").trim(),
    hotspots: parseHotspotsFromApiEntry(entry),
    activeHotspotIndex: 0,
  };
}

const COLLECTION_TILE_OPTIONS = [
  {
    id: "banner-top-right",
    label: "Top Right Banner",
    defaultMediaType: "video",
    defaultMediaUrl: "/videos/women-sofa.mp4",
  },
  {
    id: "1",
    label: "Residential",
    defaultMediaType: "image",
    defaultMediaUrl: "/photos/residential.webp",
  },
  {
    id: "2",
    label: "Workspace",
    defaultMediaType: "image",
    defaultMediaUrl: "/photos/workspacee.webp",
  },
  {
    id: "3",
    label: "Video Tile / Education",
    defaultMediaType: "video",
    defaultMediaUrl: "/videos/wd-furniture-hotspot-video-opt.mp4",
  },
  {
    id: "4",
    label: "Hospitality",
    defaultMediaType: "image",
    defaultMediaUrl: "/photos/hosipilty.webp",
  },
  {
    id: "5",
    label: "Seating",
    defaultMediaType: "image",
    defaultMediaUrl: "/photos/residential.webp",
  },
  {
    id: "6",
    label: "Storage",
    defaultMediaType: "image",
    defaultMediaUrl: "/photos/workspacee.webp",
  },
  {
    id: "7",
    label: "Tables",
    defaultMediaType: "image",
    defaultMediaUrl: "/photos/hosipilty.webp",
  },
  {
    id: "8",
    label: "Decor",
    defaultMediaType: "image",
    defaultMediaUrl: "/photos/education.webp",
  },
  {
    id: "9",
    label: "Decor (tile 9)",
    defaultMediaType: "image",
    defaultMediaUrl: "/photos/education.webp",
  },
];

function createDefaultTileMediaState() {
  return COLLECTION_TILE_OPTIONS.reduce((acc, tile) => {
    acc[tile.id] = {
      mediaType: "default",
      mediaUrl: "",
      hotspots: [
        { localId: newHotspotLocalId(), hotspotX: 50, hotspotY: 50, productId: "" },
      ],
      activeHotspotIndex: 0,
    };
    return acc;
  }, {});
}

function collectionsTileMediaHasContent(tileMedia) {
  return Object.values(tileMedia || {}).some((value) => {
    const mediaUrl = String(value?.mediaUrl || "").trim();
    const mediaType = String(value?.mediaType || "default").toLowerCase();
    const hs = Array.isArray(value?.hotspots) ? value.hotspots : [];
    const anyHotspotProduct = hs.some((h) => String(h?.productId || "").trim());
    return mediaType !== "default" || Boolean(mediaUrl) || anyHotspotProduct;
  });
}

function buildCollectionsConfigApiPayload(formSnapshot) {
  const normalizedTileMedia = Object.entries(formSnapshot.tileMedia || {})
    .filter(([tileId]) => Boolean(String(tileId || "").trim()))
    .map(([tileId, value]) => {
      const hs =
        Array.isArray(value?.hotspots) && value.hotspots.length > 0
          ? value.hotspots
          : [
              {
                hotspotX: 50,
                hotspotY: 50,
                productId: "",
              },
            ];
      return {
        tileId: String(tileId).trim(),
        mediaType: ["default", "image", "video"].includes(
          String(value?.mediaType || "").toLowerCase()
        )
          ? String(value.mediaType).toLowerCase()
          : "default",
        mediaUrl: String(value?.mediaUrl || "").trim(),
        hotspots: hs.map((h) => ({
          x: normalizePercent(h?.hotspotX ?? h?.x, 50),
          y: normalizePercent(h?.hotspotY ?? h?.y, 50),
          productId: normalizeId(h?.productId) || null,
        })),
      };
    });
  const bannerTile = normalizedTileMedia.find((item) => item.tileId === "banner-top-right");
  const legacyPhotoUrl =
    bannerTile && bannerTile.mediaType === "image" ? String(bannerTile.mediaUrl || "") : "";
  const firstBannerHs = bannerTile?.hotspots?.[0];
  const legacyHotspot = firstBannerHs
    ? {
        x: firstBannerHs.x,
        y: firstBannerHs.y,
        productId: firstBannerHs.productId,
      }
    : {
        x: 50,
        y: 50,
        productId: null,
      };

  return {
    photoUrl: legacyPhotoUrl,
    hotspot: legacyHotspot,
    tileMedia: normalizedTileMedia,
    heroSlides: Object.entries(formSnapshot.heroSlides || {})
      .map(([category, raw]) => {
        const {
          desktop, mobile, desktopAr, mobileAr, productImage, heroProductId, heroProductItems,
          heroProductX, heroProductY, heroProductScale,
          heroBtnX, heroBtnY, heroBtnMobileX, heroBtnMobileY,
          heroProductMobileX, heroProductMobileY, heroProductMobileScale,
          heroTextX, heroTextY,
          heroProductXAr, heroProductYAr, heroProductScaleAr,
          heroTextXAr, heroTextYAr,
          heroBtnXAr, heroBtnYAr,
          heroProductMobileXAr, heroProductMobileYAr, heroProductMobileScaleAr,
          heroBtnMobileXAr, heroBtnMobileYAr,
          heroTitleEn, heroTitleAr, heroSubtitleEn, heroSubtitleAr,
          heroTrustItems,
          heroDotsX, heroDotsY,
          heroDotsMobileX, heroDotsMobileY,
        } = normalizeHeroSlideFormValue(raw);
        return {
          category: String(category || "").trim().toLowerCase(),
          backgroundUrl: desktop,
          backgroundUrlMobile: mobile,
          backgroundUrlAr: desktopAr,
          backgroundUrlArMobile: mobileAr,
          productImageUrl: productImage,
          heroProductId,
          heroProductItems: Array.isArray(heroProductItems)
            ? heroProductItems.map(normalizeHeroProductItemForm).filter(Boolean)
            : [],
          heroProductX: Number(heroProductX) || 0,
          heroProductY: Number(heroProductY) || 0,
          heroProductScale: Math.max(0.1, Number(heroProductScale) || 1),
          heroBtnX: Number(heroBtnX) || 0,
          heroBtnY: Number(heroBtnY) || 0,
          heroBtnMobileX: Number(heroBtnMobileX) || 0,
          heroBtnMobileY: Number(heroBtnMobileY) || 0,
          heroProductMobileX: Number(heroProductMobileX) || 0,
          heroProductMobileY: Number(heroProductMobileY) || 0,
          heroProductMobileScale: Math.max(0.1, Number(heroProductMobileScale) || 1),
          heroTextX: Number(heroTextX) || 0,
          heroTextY: Number(heroTextY) || 0,
          heroProductXAr: Number(heroProductXAr) || 0,
          heroProductYAr: Number(heroProductYAr) || 0,
          heroProductScaleAr: Math.max(0.1, Number(heroProductScaleAr) || 1),
          heroTextXAr: Number(heroTextXAr) || 0,
          heroTextYAr: Number(heroTextYAr) || 0,
          heroBtnXAr: Number(heroBtnXAr) || 0,
          heroBtnYAr: Number(heroBtnYAr) || 0,
          heroProductMobileXAr: Number(heroProductMobileXAr) || 0,
          heroProductMobileYAr: Number(heroProductMobileYAr) || 0,
          heroProductMobileScaleAr: Math.max(0.1, Number(heroProductMobileScaleAr) || 1),
          heroBtnMobileXAr: Number(heroBtnMobileXAr) || 0,
          heroBtnMobileYAr: Number(heroBtnMobileYAr) || 0,
          heroTitleEn: String(heroTitleEn || ""),
          heroTitleAr: String(heroTitleAr || ""),
          heroSubtitleEn: String(heroSubtitleEn || ""),
          heroSubtitleAr: String(heroSubtitleAr || ""),
          heroTrustItems: normalizeTrustItems(heroTrustItems),
          heroDotsX: Number(heroDotsX) || 0,
          heroDotsY: Math.max(0, Math.min(200, Number(heroDotsY ?? 16) || 0)),
          heroDotsMobileX: Number(heroDotsMobileX) || 0,
          heroDotsMobileY: Math.max(0, Math.min(200, Number(heroDotsMobileY ?? 10) || 0)),
        };
      })
      .filter((s) => Boolean(s.category)),
  };
}

function centerAspectCrop(mediaWidth, mediaHeight, aspect = 1) {
  return centerCrop(
    makeAspectCrop(
      { unit: "%", width: 90 },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );
}

function getCroppedImageFile(imageElement, crop, fileName = "image.jpg", options = {}) {
  const { isIcon = false, isSvg = false } = options;
  return new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      reject(new Error("Failed to initialize image editor."));
      return;
    }
    const displayWidth = imageElement.width || imageElement.naturalWidth;
    const displayHeight = imageElement.height || imageElement.naturalHeight;
    if (!displayWidth || !displayHeight) {
      reject(new Error("Failed to read image dimensions."));
      return;
    }
    const scaleX = imageElement.naturalWidth / displayWidth;
    const scaleY = imageElement.naturalHeight / displayHeight;
    const cropX = Number.isFinite(crop?.x) ? crop.x : 0;
    const cropY = Number.isFinite(crop?.y) ? crop.y : 0;
    const cropWidth = Number.isFinite(crop?.width) ? crop.width : 0;
    const cropHeight = Number.isFinite(crop?.height) ? crop.height : 0;
    const srcX = Math.max(0, Math.floor(cropX * scaleX));
    const srcY = Math.max(0, Math.floor(cropY * scaleY));
    const srcWidth = Math.min(
      imageElement.naturalWidth - srcX,
      Math.max(1, Math.round(cropWidth * scaleX))
    );
    const srcHeight = Math.min(
      imageElement.naturalHeight - srcY,
      Math.max(1, Math.round(cropHeight * scaleY))
    );
    if (srcWidth < 1 || srcHeight < 1) {
      reject(new Error("Crop area is too small."));
      return;
    }
    const exportScale = isIcon ? (isSvg ? 4 : 2) : 1;
    const targetWidth = Math.max(1, Math.round(srcWidth * exportScale));
    const targetHeight = Math.max(1, Math.round(srcHeight * exportScale));
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(
      imageElement,
      srcX,
      srcY,
      srcWidth,
      srcHeight,
      0,
      0,
      targetWidth,
      targetHeight
    );
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Failed to crop image."));
          return;
        }
        const cleanName = fileName.replace(/\.[^.]+$/, "") || "image";
        resolve(new File([blob], `${cleanName}-cropped.png`, { type: "image/png" }));
      },
      "image/png",
      0.95
    );
  });
}

function StoreControlPanel({
  sectionId,
  isOpen,
  onToggle,
  title,
  description,
  badge,
  children,
}) {
  const bodyId = `store-panel-body-${sectionId}`;
  const triggerId = `store-panel-trigger-${sectionId}`;
  return (
    <section className={`store-control-panel ${isOpen ? "is-open" : ""}`}>
      <button
        type="button"
        id={triggerId}
        className="store-control-panel-trigger"
        onClick={() => onToggle(sectionId)}
        aria-expanded={isOpen}
        aria-controls={bodyId}
      >
        <div className="store-control-panel-trigger-text">
          <h2 className="store-control-panel-title">{title}</h2>
          <p className="store-control-panel-desc">{description}</p>
        </div>
        {badge != null && String(badge).length > 0 ? (
          <span className="store-control-panel-badge">{badge}</span>
        ) : null}
        <span className="store-control-panel-chevron" aria-hidden />
      </button>
      <div
        id={bodyId}
        role="region"
        aria-labelledby={triggerId}
        className="store-control-panel-body"
        hidden={!isOpen}
      >
        {isOpen ? children : null}
      </div>
    </section>
  );
}

export default function StoreControl() {
  const navigate = useNavigate();
  const token =
    localStorage.getItem("authToken") || sessionStorage.getItem("authToken");

  const [rows, setRows] = useState([]);
  const [heroProductSearch, setHeroProductSearch] = useState("");
  const heroPosPreviewRef = useRef(null);
  const heroPosDragRef = useRef(null);
  const [heroPosDragging, setHeroPosDragging] = useState(false);
  const heroBtnPreviewRef = useRef(null);
  const heroBtnDragRef = useRef(null);
  const [heroBtnDragging, setHeroBtnDragging] = useState(false);
  const heroTextDragRef = useRef(null);
  const [heroTextDragging, setHeroTextDragging] = useState(false);
  const [heroEditorLang, setHeroEditorLang] = useState("en");
  const [showHeroProductPicker, setShowHeroProductPicker] = useState(false);
  const [expandedHeroProductId, setExpandedHeroProductId] = useState(null);
  const [openSection, setOpenSection] = useState("hero");
  const toggleStoreSection = useCallback((sectionId) => {
    setOpenSection((prev) => (prev === sectionId ? null : sectionId));
  }, []);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bundleExportLoading, setBundleExportLoading] = useState(false);
  const [bundleImportLoading, setBundleImportLoading] = useState(false);
  const [bundleApplyLoading, setBundleApplyLoading] = useState(false);
  const [pendingStoreBundle, setPendingStoreBundle] = useState(null);
  const storeBundleInputRef = useRef(null);
  const [categoryForm, setCategoryForm] = useState({
    label: "",
    arLabel: "",
    slug: "",
    parentId: "",
    isActive: true,
    iconUrl: "",
    coverImageUrl: "",
    storeHomeImageUrl: "",
    seoTitle: "",
    seoDescription: "",
    serialGroupCode: "",
  });
  const [savingCategory, setSavingCategory] = useState(false);
  const [savingCollections, setSavingCollections] = useState(false);
  const [savingHeroBackground, setSavingHeroBackground] = useState(false);
  const [uploadingField, setUploadingField] = useState("");
  const [draggedCategoryId, setDraggedCategoryId] = useState(null);
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [adminCatsOpen, setAdminCatsOpen] = useState(() => new Set());
  /** Hero storefront frame uses 4/5 — match crop guide (Store.css `.hero-product-visual`). */
  const HERO_IMAGE_CROP_ASPECT = 4 / 5;

  const [cropModal, setCropModal] = useState({
    open: false,
    src: "",
    fieldName: "",
    fileName: "image.jpg",
    sourceType: "",
    isObjectUrl: false,
    heroTarget: null,
  });
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState();
  const [cropAspect, setCropAspect] = useState(1);
  const [imgRef, setImgRef] = useState(null);
  const [tilePreviewMediaSize, setTilePreviewMediaSize] = useState({});
  const [collectionsForm, setCollectionsForm] = useState({
    photoUrl: "",
    hotspotX: 50,
    hotspotY: 50,
    productId: "",
    tileMedia: createDefaultTileMediaState(),
    heroSlides: {
      default: { desktop: "", mobile: "" },
    },
  });
  const collectionsFormRef = useRef(collectionsForm);
  useEffect(() => {
    collectionsFormRef.current = collectionsForm;
  }, [collectionsForm]);

  const applySavedCollectionsResponse = useCallback((savedConfig) => {
    const savedTileMediaMap = createDefaultTileMediaState();
    if (Array.isArray(savedConfig?.tileMedia)) {
      savedConfig.tileMedia.forEach((entry) => {
        const key = String(entry?.tileId || "").trim();
        if (!key) return;
        savedTileMediaMap[key] = tileMediaStateFromApiEntry(entry);
      });
    }
    setCollectionsForm((prev) => ({
      ...prev,
      photoUrl: String(savedConfig?.photoUrl || "").trim(),
      hotspotX: normalizePercent(savedConfig?.hotspot?.x, prev.hotspotX),
      hotspotY: normalizePercent(savedConfig?.hotspot?.y, prev.hotspotY),
      productId: normalizeId(savedConfig?.hotspot?.productId || prev.productId || ""),
      tileMedia: savedTileMediaMap,
      heroSlides: {
        default: normalizeHeroSlideFormValue(null),
        ...(prev.heroSlides || {}),
        ...(Array.isArray(savedConfig?.heroSlides)
          ? savedConfig.heroSlides.reduce((acc, s) => {
              const key = String(s?.category || "").trim().toLowerCase();
              if (!key) return acc;
              acc[key] = {
                desktop: String(s?.backgroundUrl || "").trim(),
                mobile: String(s?.backgroundUrlMobile || "").trim(),
                desktopAr: String(s?.backgroundUrlAr || "").trim(),
                mobileAr: String(s?.backgroundUrlArMobile || "").trim(),
                productImage: String(s?.productImageUrl || "").trim(),
                heroProductId: String(s?.heroProductId || "").trim(),
                heroProductItems: Array.isArray(s?.heroProductItems)
                  ? s.heroProductItems.map(normalizeHeroProductItemForm).filter(Boolean)
                  : [],
                heroProductX: Number(s?.heroProductX ?? 0) || 0,
                heroProductY: Number(s?.heroProductY ?? 0) || 0,
                heroProductScale: Math.max(0.1, Number(s?.heroProductScale ?? 1) || 1),
                heroBtnX: Number(s?.heroBtnX ?? 0) || 0,
                heroBtnY: Number(s?.heroBtnY ?? 0) || 0,
                heroBtnMobileX: Number(s?.heroBtnMobileX ?? 0) || 0,
                heroBtnMobileY: Number(s?.heroBtnMobileY ?? 0) || 0,
                heroProductMobileX: Number(s?.heroProductMobileX ?? 0) || 0,
                heroProductMobileY: Number(s?.heroProductMobileY ?? 0) || 0,
                heroProductMobileScale: Math.max(0.1, Number(s?.heroProductMobileScale ?? 1) || 1),
                heroTextX: Number(s?.heroTextX ?? 0) || 0,
                heroTextY: Number(s?.heroTextY ?? 0) || 0,
                heroProductXAr: Number(s?.heroProductXAr ?? 0) || 0,
                heroProductYAr: Number(s?.heroProductYAr ?? 0) || 0,
                heroProductScaleAr: Math.max(0.1, Number(s?.heroProductScaleAr ?? 1) || 1),
                heroTextXAr: Number(s?.heroTextXAr ?? 0) || 0,
                heroTextYAr: Number(s?.heroTextYAr ?? 0) || 0,
                heroBtnXAr: Number(s?.heroBtnXAr ?? 0) || 0,
                heroBtnYAr: Number(s?.heroBtnYAr ?? 0) || 0,
                heroProductMobileXAr: Number(s?.heroProductMobileXAr ?? 0) || 0,
                heroProductMobileYAr: Number(s?.heroProductMobileYAr ?? 0) || 0,
                heroProductMobileScaleAr: Math.max(0.1, Number(s?.heroProductMobileScaleAr ?? 1) || 1),
                heroBtnMobileXAr: Number(s?.heroBtnMobileXAr ?? 0) || 0,
                heroBtnMobileYAr: Number(s?.heroBtnMobileYAr ?? 0) || 0,
                heroTitleEn: String(s?.heroTitleEn || ""),
                heroTitleAr: String(s?.heroTitleAr || ""),
                heroSubtitleEn: String(s?.heroSubtitleEn || ""),
                heroSubtitleAr: String(s?.heroSubtitleAr || ""),
                heroTrustItems: normalizeTrustItems(s?.heroTrustItems),
                heroDotsX: Number(s?.heroDotsX ?? 0) || 0,
                heroDotsY: Math.max(0, Math.min(200, Number(s?.heroDotsY ?? 16) || 0)),
                heroDotsMobileX: Number(s?.heroDotsMobileX ?? 0) || 0,
                heroDotsMobileY: Math.max(0, Math.min(200, Number(s?.heroDotsMobileY ?? 10) || 0)),
              };
              return acc;
            }, {})
          : {}),
      },
    }));
  }, []);

  const persistCollectionsFormSnapshot = useCallback(
    async (formSnapshot, { skipContentCheck = false, successMessage } = {}) => {
      if (!skipContentCheck && !collectionsTileMediaHasContent(formSnapshot.tileMedia)) {
        return { ok: false, reason: "empty" };
      }
      const payload = buildCollectionsConfigApiPayload(formSnapshot);
      console.log("[HERO SAVE] payload heroSlides:", JSON.stringify(payload.heroSlides));
      try {
        const { data } = await axios.put("/store/products/collections-config", payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const savedConfig = data?.config || payload;
        console.log("[HERO SAVE] response heroSlides:", JSON.stringify(savedConfig?.heroSlides));
        applySavedCollectionsResponse(savedConfig);
        if (successMessage) toast.success(successMessage);
        return { ok: true };
      } catch (error) {
        return { ok: false, error };
      }
    },
    [token, applySavedCollectionsResponse]
  );

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      const [categoriesResponse, collectionsResponse, productsResponse] = await Promise.all([
        axios.get("/store/categories"),
        axios.get("/store/products/collections-config", {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get("/store/products"),
      ]);
        const list = Array.isArray(productsResponse?.data) ? productsResponse.data : [];
        const categoryList = Array.isArray(categoriesResponse.data)
          ? categoriesResponse.data
          : [];
        setRows(
          list.map((item) => ({
            _id: item._id,
            name: item.name || "-",
            arName: item.arName || "",
            category: item.category || "-",
            images: Array.isArray(item?.images) ? item.images : [],
            image: item?.image || null,
            imageCount: Array.isArray(item?.images) ? item.images.length : 0,
            heroImageIndex: normalizeHeroImageIndex(item?.heroImageIndex, 0),
            heroImageFit: normalizeHeroImageFit(item?.heroImageFit, "cover"),
            heroImagePositionX: clampPercent(item?.heroImagePositionX, 50),
            heroImagePositionY: clampPercent(item?.heroImagePositionY, 50),
            thumbnailUrl: pickProductThumbnail(item, item?.heroImageIndex),
            heroEnabled: Boolean(item.heroEnabled),
            heroOrder: normalizeHeroOrder(item.heroOrder, 0),
          }))
        );
        setCategories(
          [...categoryList].sort(
            (a, b) => Number(a?.sortOrder || 0) - Number(b?.sortOrder || 0)
          )
        );
        const savedCollections = collectionsResponse?.data || {};
        const savedTileMediaMap = createDefaultTileMediaState();
        if (Array.isArray(savedCollections?.tileMedia)) {
          savedCollections.tileMedia.forEach((entry) => {
            const key = String(entry?.tileId || "").trim();
            if (!key) return;
            savedTileMediaMap[key] = tileMediaStateFromApiEntry(entry);
          });
        }
        const legacyBannerTile = savedTileMediaMap["banner-top-right"];
        const legacyPhoto = String(savedCollections?.photoUrl || "").trim();
        if (legacyPhoto && (!legacyBannerTile?.mediaUrl || legacyBannerTile.mediaType === "default")) {
          const baseHotspots =
            Array.isArray(legacyBannerTile?.hotspots) && legacyBannerTile.hotspots.length
              ? legacyBannerTile.hotspots.map((h, i) =>
                  i === 0
                    ? {
                        ...h,
                        hotspotX: normalizePercent(
                          savedCollections?.hotspot?.x,
                          h.hotspotX
                        ),
                        hotspotY: normalizePercent(
                          savedCollections?.hotspot?.y,
                          h.hotspotY
                        ),
                        productId: normalizeId(
                          savedCollections?.hotspot?.productId || h.productId || ""
                        ),
                      }
                    : h
                )
              : parseHotspotsFromApiEntry({
                  hotspot: savedCollections?.hotspot,
                });
          savedTileMediaMap["banner-top-right"] = {
            ...legacyBannerTile,
            mediaType: "image",
            mediaUrl: legacyPhoto,
            hotspots: baseHotspots,
            activeHotspotIndex: legacyBannerTile?.activeHotspotIndex ?? 0,
          };
        }
        setCollectionsForm({
          photoUrl: String(savedCollections?.photoUrl || "").trim(),
          hotspotX: normalizePercent(savedCollections?.hotspot?.x, 50),
          hotspotY: normalizePercent(savedCollections?.hotspot?.y, 50),
          productId: normalizeId(savedCollections?.hotspot?.productId),
          tileMedia: savedTileMediaMap,
          heroSlides: {
            default: normalizeHeroSlideFormValue(null),
            ...(Array.isArray(savedCollections?.heroSlides)
              ? savedCollections.heroSlides.reduce((acc, s) => {
                  const key = String(s?.category || "").trim().toLowerCase();
                  if (!key) return acc;
                  acc[key] = {
                    desktop: String(s?.backgroundUrl || "").trim(),
                    mobile: String(s?.backgroundUrlMobile || "").trim(),
                    desktopAr: String(s?.backgroundUrlAr || "").trim(),
                    mobileAr: String(s?.backgroundUrlArMobile || "").trim(),
                    productImage: String(s?.productImageUrl || "").trim(),
                    heroProductId: String(s?.heroProductId || "").trim(),
                    heroProductItems: Array.isArray(s?.heroProductItems)
                      ? s.heroProductItems.map(normalizeHeroProductItemForm).filter(Boolean)
                      : [],
                    heroProductX: Number(s?.heroProductX ?? 0) || 0,
                    heroProductY: Number(s?.heroProductY ?? 0) || 0,
                    heroProductScale: Math.max(0.1, Number(s?.heroProductScale ?? 1) || 1),
                    heroBtnX: Number(s?.heroBtnX ?? 0) || 0,
                    heroBtnY: Number(s?.heroBtnY ?? 0) || 0,
                    heroBtnMobileX: Number(s?.heroBtnMobileX ?? 0) || 0,
                    heroBtnMobileY: Number(s?.heroBtnMobileY ?? 0) || 0,
                    heroProductMobileX: Number(s?.heroProductMobileX ?? 0) || 0,
                    heroProductMobileY: Number(s?.heroProductMobileY ?? 0) || 0,
                    heroProductMobileScale: Math.max(0.1, Number(s?.heroProductMobileScale ?? 1) || 1),
                    heroTextX: Number(s?.heroTextX ?? 0) || 0,
                    heroTextY: Number(s?.heroTextY ?? 0) || 0,
                    heroProductXAr: Number(s?.heroProductXAr ?? 0) || 0,
                    heroProductYAr: Number(s?.heroProductYAr ?? 0) || 0,
                    heroProductScaleAr: Math.max(0.1, Number(s?.heroProductScaleAr ?? 1) || 1),
                    heroTextXAr: Number(s?.heroTextXAr ?? 0) || 0,
                    heroTextYAr: Number(s?.heroTextYAr ?? 0) || 0,
                    heroBtnXAr: Number(s?.heroBtnXAr ?? 0) || 0,
                    heroBtnYAr: Number(s?.heroBtnYAr ?? 0) || 0,
                    heroProductMobileXAr: Number(s?.heroProductMobileXAr ?? 0) || 0,
                    heroProductMobileYAr: Number(s?.heroProductMobileYAr ?? 0) || 0,
                    heroProductMobileScaleAr: Math.max(0.1, Number(s?.heroProductMobileScaleAr ?? 1) || 1),
                    heroBtnMobileXAr: Number(s?.heroBtnMobileXAr ?? 0) || 0,
                    heroBtnMobileYAr: Number(s?.heroBtnMobileYAr ?? 0) || 0,
                    heroTitleEn: String(s?.heroTitleEn || ""),
                    heroTitleAr: String(s?.heroTitleAr || ""),
                    heroSubtitleEn: String(s?.heroSubtitleEn || ""),
                    heroSubtitleAr: String(s?.heroSubtitleAr || ""),
                    heroTrustItems: normalizeTrustItems(s?.heroTrustItems),
                    heroDotsX: Number(s?.heroDotsX ?? 0) || 0,
                    heroDotsY: Math.max(0, Math.min(200, Number(s?.heroDotsY ?? 16) || 0)),
                    heroDotsMobileX: Number(s?.heroDotsMobileX ?? 0) || 0,
                    heroDotsMobileY: Math.max(0, Math.min(200, Number(s?.heroDotsMobileY ?? 10) || 0)),
                  };
                  return acc;
                }, {})
              : {}),
          },
        });
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to load store control.");
    } finally {
      setLoading(false);
    }
  }, [token, applySavedCollectionsResponse]);

  useEffect(() => {
    if (!token) {
      navigate("/dashboard/login");
      return;
    }
    fetchConfig();
  }, [token, navigate, fetchConfig]);

  const downloadStoreBundle = async () => {
    try {
      setBundleExportLoading(true);
      const { data } = await axios.get("/store/bundle/export", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = JSON.stringify(data, null, 2);
      const blob = new Blob([json], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `store-backup-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Store backup downloaded.");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to export store backup.");
    } finally {
      setBundleExportLoading(false);
    }
  };

  const onPickStoreBundleFile = async (file) => {
    if (!file) return;
    try {
      setBundleImportLoading(true);
      const text = await file.text();
      const parsed = JSON.parse(text);
      if (!parsed || typeof parsed !== "object") {
        throw new Error("Invalid JSON file.");
      }
      if (!parsed.version || !parsed.store) {
        throw new Error("This JSON does not look like a store backup.");
      }
      setPendingStoreBundle(parsed);
      toast.success("Backup loaded. Review then click Apply.");
    } catch (e) {
      toast.error(e?.message || "Invalid backup file.");
      setPendingStoreBundle(null);
    } finally {
      setBundleImportLoading(false);
    }
  };

  const applyStoreBundle = async () => {
    if (!pendingStoreBundle) return;
    try {
      setBundleApplyLoading(true);
      await axios.post("/store/bundle/import", pendingStoreBundle, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Store backup imported.");
      setPendingStoreBundle(null);
      await fetchConfig();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to import store backup.");
    } finally {
      setBundleApplyLoading(false);
    }
  };

  const productOptions = useMemo(
    () =>
      rows
        .map((row) => ({
          _id: row._id,
          name: row.name,
          category: row.category,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [rows]
  );
  const collectionsTiles = useMemo(
    () =>
      COLLECTION_TILE_OPTIONS.map((tile) => {
        const saved = collectionsForm.tileMedia?.[tile.id] || {};
        const mediaType = ["default", "image", "video"].includes(saved.mediaType)
          ? saved.mediaType
          : "default";
        const mediaUrl = String(saved.mediaUrl || "").trim();
        const effectiveMediaType = mediaType === "default" ? tile.defaultMediaType : mediaType;
        const effectiveMediaUrl =
          mediaType === "default" || !mediaUrl ? tile.defaultMediaUrl : mediaUrl;
        let hotspotsList =
          Array.isArray(saved.hotspots) && saved.hotspots.length > 0
            ? saved.hotspots
            : [
                {
                  localId: `default-${tile.id}`,
                  hotspotX: 50,
                  hotspotY: 50,
                  productId: "",
                },
              ];
        const activeIdx = Math.min(
          Math.max(0, Number(saved.activeHotspotIndex) || 0),
          hotspotsList.length - 1
        );
        return {
          ...tile,
          mediaType,
          mediaUrl,
          hotspotsList,
          activeHotspotIndex: activeIdx,
          effectiveMediaType,
          effectiveMediaUrl,
        };
      }),
    [collectionsForm.tileMedia]
  );

  const updateRow = (_id, next) => {
    setRows((prev) =>
      prev.map((row) => (row._id === _id ? { ...row, ...next } : row))
    );
  };

  // Hero product selection (carousel / featured product) is no longer used.

  const resetCategoryForm = () => {
    setCategoryForm({
      label: "",
      arLabel: "",
      slug: "",
      parentId: "",
      isActive: true,
      iconUrl: "",
      coverImageUrl: "",
      storeHomeImageUrl: "",
      seoTitle: "",
      seoDescription: "",
      serialGroupCode: "",
    });
    setEditingCategoryId(null);
  };

  const handleSubmitCategory = async (e) => {
    e.preventDefault();
    if (!categoryForm.label.trim()) {
      toast.error("Category name is required.");
      return;
    }

    try {
      setSavingCategory(true);
      const coverImageUrl = categoryForm.coverImageUrl.trim();
      const storeHomeImageUrl = categoryForm.storeHomeImageUrl.trim();
      const payload = {
        label: categoryForm.label.trim(),
        arLabel: categoryForm.arLabel.trim(),
        slug: categoryForm.slug.trim(),
        parentId: categoryForm.parentId || null,
        isActive: Boolean(categoryForm.isActive),
        iconUrl: categoryForm.iconUrl.trim(),
        coverImageUrl,
        storeHomeImageUrl,
        imageUrl: coverImageUrl || storeHomeImageUrl,
        seoTitle: categoryForm.seoTitle.trim(),
        seoDescription: categoryForm.seoDescription.trim(),
        serialGroupCode: String(categoryForm.serialGroupCode || "")
          .trim()
          .toUpperCase()
          .slice(0, 2),
      };
      if (editingCategoryId) {
        const current = categories.find((c) => c._id === editingCategoryId);
        const { data } = await axios.put(
          `/store/categories/${editingCategoryId}`,
          { ...payload, sortOrder: Number(current?.sortOrder || 0) },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setCategories((prev) =>
          prev
            .map((c) => (c._id === editingCategoryId ? data : c))
            .sort((a, b) => Number(a?.sortOrder || 0) - Number(b?.sortOrder || 0))
        );
        toast.success("Category updated.");
      } else {
        const { data } = await axios.post("/store/categories", payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCategories((prev) =>
          [...prev, data].sort(
            (a, b) => Number(a?.sortOrder || 0) - Number(b?.sortOrder || 0)
          )
        );
        toast.success("Category added.");
      }
      resetCategoryForm();
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          `Failed to ${editingCategoryId ? "update" : "add"} category.`
      );
    } finally {
      setSavingCategory(false);
    }
  };

  const uploadCategoryFile = async (file, fieldName) => {
    const isCollectionsField =
      fieldName === "collectionsPhotoUrl" || fieldName.startsWith("collectionsTile:");
    if (!file) {
      toast.error("Please select a file.");
      return;
    }
    if (isCollectionsField) {
      const isValidCollectionsType =
        file.type?.startsWith("image/") || file.type?.startsWith("video/");
      if (!isValidCollectionsType) {
        toast.error("Please upload a valid image or video file.");
        return;
      }
    } else if (!file.type?.startsWith("image/")) {
      toast.error("Please drop a valid image file.");
      return;
    }
    try {
      setUploadingField(fieldName);
      const form = new FormData();
      if (isCollectionsField) {
        form.append("media", file);
      } else {
        form.append("image", file);
      }
      const { data } = await axios.post(
        isCollectionsField ? "/store/products/collections-upload" : "/store/categories/upload",
        form,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      const uploadedUrl = data?.url || "";
      if (fieldName === "collectionsPhotoUrl") {
        setCollectionsForm((prev) => ({ ...prev, photoUrl: uploadedUrl }));
        toast.success("Collections photo uploaded.");
      } else if (fieldName.startsWith("collectionsTile:")) {
        const tileId = fieldName.slice("collectionsTile:".length);
        const isVideo = file.type?.startsWith("video/");
        const prev = collectionsFormRef.current;
        const nextSnapshot = {
          ...prev,
          tileMedia: {
            ...prev.tileMedia,
            [tileId]: {
              ...(prev.tileMedia?.[tileId] || {}),
              mediaType: isVideo ? "video" : "image",
              mediaUrl: uploadedUrl,
            },
          },
        };
        collectionsFormRef.current = nextSnapshot;
        setCollectionsForm(nextSnapshot);
        const persistResult = await persistCollectionsFormSnapshot(nextSnapshot, {
          skipContentCheck: true,
          successMessage: "Collection tile media uploaded and saved.",
        });
        if (!persistResult.ok) {
          toast.error(
            persistResult.error?.response?.data?.message ||
              "Media uploaded, but saving to collections config failed. Use “Save collections media” to retry."
          );
        }
      } else {
        setCategoryForm((prev) => ({ ...prev, [fieldName]: uploadedUrl }));
        const uploadLabels = {
          iconUrl: "Category logo",
          coverImageUrl: "Category page cover",
          storeHomeImageUrl: "Store home cover",
        };
        toast.success(`${uploadLabels[fieldName] || "Image"} uploaded.`);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Media upload failed.");
    } finally {
      setUploadingField("");
    }
  };

  const updateTileMedia = (tileId, nextValues) => {
    setCollectionsForm((prev) => ({
      ...prev,
      tileMedia: {
        ...prev.tileMedia,
        [tileId]: {
          ...(prev.tileMedia?.[tileId] || {}),
          ...nextValues,
        },
      },
    }));
  };

  const closeCropModal = () => {
    if (cropModal.isObjectUrl && cropModal.src) {
      URL.revokeObjectURL(cropModal.src);
    }
    setCropModal({
      open: false,
      src: "",
      fieldName: "",
      fileName: "image.jpg",
      sourceType: "",
      isObjectUrl: false,
      heroTarget: null,
    });
    setCrop(undefined);
    setCompletedCrop(undefined);
  };

  const openCropModalForFile = (file, fieldName, options = {}) => {
    const { aspect, heroTarget } = options;
    if (!file || !file.type?.startsWith("image/")) {
      toast.error("Please select a valid image file.");
      return;
    }
    const src = URL.createObjectURL(file);
    setCropAspect(aspect !== undefined && aspect !== null ? aspect : undefined);
    setCrop(undefined);
    setCompletedCrop(undefined);
    setCropModal({
      open: true,
      src,
      fieldName,
      fileName: file.name || "image.jpg",
      sourceType: file.type || "",
      isObjectUrl: true,
      heroTarget: heroTarget ?? null,
    });
  };

  const openHeroRowImageCrop = async (row) => {
    const imageIndex = normalizeHeroImageIndex(row.heroImageIndex, 0);
    const rawUrl = pickProductThumbnail(row, imageIndex);
    if (!rawUrl || !String(rawUrl).trim()) {
      toast.error("No image to crop for this product.");
      return;
    }
    try {
      const fetchUrl =
        /^https?:\/\//i.test(rawUrl) ? rawUrl : new URL(rawUrl, window.location.origin).href;
      const response = await fetch(fetchUrl);
      if (!response.ok) throw new Error("Failed to load image.");
      const blob = await response.blob();
      if (!blob.type?.startsWith("image/")) {
        toast.error("Only images can be cropped.");
        return;
      }
      const file = new File([blob], `hero-${row._id}.png`, {
        type: blob.type || "image/png",
      });
      openCropModalForFile(file, "heroProductImage", {
        aspect: HERO_IMAGE_CROP_ASPECT,
        heroTarget: { productId: row._id, imageIndex },
      });
    } catch (error) {
      toast.error(error?.message || "Failed to open image editor.");
    }
  };

  const onCropImageLoad = useCallback(
    (e) => {
      const { naturalWidth, naturalHeight, width, height } = e.currentTarget;
      if (cropAspect) {
        const initialCrop = centerAspectCrop(naturalWidth, naturalHeight, cropAspect);
        setCrop(initialCrop);
        setCompletedCrop({
          unit: "px",
          x: (initialCrop.x / 100) * width,
          y: (initialCrop.y / 100) * height,
          width: (initialCrop.width / 100) * width,
          height: (initialCrop.height / 100) * height,
        });
      } else {
        const fullCrop = { unit: "%", x: 5, y: 5, width: 90, height: 90 };
        setCrop(fullCrop);
        setCompletedCrop({
          unit: "px",
          x: (fullCrop.x / 100) * width,
          y: (fullCrop.y / 100) * height,
          width: (fullCrop.width / 100) * width,
          height: (fullCrop.height / 100) * height,
        });
      }
    },
    [cropAspect]
  );

  const uploadHeroProductCroppedImage = async (file, { productId, imageIndex }) => {
    if (!file || !productId) return;
    try {
      setUploadingField("heroProductImage");
      const form = new FormData();
      form.append("images", file);
      form.append("replaceImageIndex", String(imageIndex));
      const { data } = await axios.put(`/store/products/${productId}`, form, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      const item = data;
      setRows((prev) =>
        prev.map((r) =>
          r._id === productId
            ? {
                ...r,
                images: Array.isArray(item?.images) ? item.images : [],
                image: item?.image || null,
                imageCount: Array.isArray(item?.images) ? item.images.length : 0,
                heroImageIndex: normalizeHeroImageIndex(item?.heroImageIndex, r.heroImageIndex),
                heroImageFit: normalizeHeroImageFit(item?.heroImageFit, r.heroImageFit),
                heroImagePositionX: clampPercent(item?.heroImagePositionX, r.heroImagePositionX),
                heroImagePositionY: clampPercent(item?.heroImagePositionY, r.heroImagePositionY),
                thumbnailUrl: pickProductThumbnail(item, item?.heroImageIndex ?? r.heroImageIndex),
                heroEnabled: Boolean(item.heroEnabled),
                heroOrder: normalizeHeroOrder(item.heroOrder, r.heroOrder),
              }
            : r
        )
      );
      toast.success("Hero image cropped and saved.");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to save cropped image.");
    } finally {
      setUploadingField("");
    }
  };

  const handleApplyCrop = async () => {
    if (!cropModal.src || !completedCrop || !imgRef) return;
    try {
      const croppedFile = await getCroppedImageFile(
        imgRef,
        completedCrop,
        cropModal.fileName,
        {
          isIcon: cropModal.fieldName === "iconUrl",
          isSvg:
            cropModal.sourceType === "image/svg+xml" ||
            /\.svg$/i.test(cropModal.fileName || ""),
        }
      );
      if (cropModal.fieldName === "heroProductImage" && cropModal.heroTarget) {
        await uploadHeroProductCroppedImage(croppedFile, cropModal.heroTarget);
        closeCropModal();
        return;
      }
      await uploadCategoryFile(croppedFile, cropModal.fieldName);
      closeCropModal();
    } catch (error) {
      toast.error(error?.message || "Failed to apply crop.");
    }
  };

  const handleEditExistingCrop = async (fieldName) => {
    const isCollectionTileField = fieldName.startsWith("collectionsTile:");
    const tileId = isCollectionTileField ? fieldName.slice("collectionsTile:".length) : "";
    const tileConfig = isCollectionTileField ? collectionsForm.tileMedia?.[tileId] || {} : null;
    const imageUrl =
      fieldName === "collectionsPhotoUrl" ? collectionsForm.photoUrl : categoryForm[fieldName];
    const resolvedImageUrl = isCollectionTileField
      ? String(tileConfig?.mediaUrl || "").trim()
      : imageUrl;
    if (!resolvedImageUrl) return;
    if (isCollectionTileField && tileConfig?.mediaType === "video") {
      toast.error("Video media cannot be cropped. Switch this tile to image first.");
      return;
    }
    try {
      const response = await fetch(resolvedImageUrl);
      if (!response.ok) throw new Error("Failed to load image.");
      const blob = await response.blob();
      if (!blob.type?.startsWith("image/")) {
        toast.error("Only images can be cropped.");
        return;
      }
      const ext = blob.type === "image/svg+xml" ? "svg" : "png";
      const fallbackName =
        fieldName === "iconUrl"
          ? `icon.${ext}`
          : fieldName === "coverImageUrl"
          ? `category-cover.${ext}`
          : fieldName === "storeHomeImageUrl"
          ? `category-store-home.${ext}`
          : fieldName === "collectionsPhotoUrl"
          ? `collections.${ext}`
          : isCollectionTileField
          ? `collection-tile-${tileId}.${ext}`
          : `category.${ext}`;
      const file = new File([blob], fallbackName, {
        type: blob.type || "image/png",
      });
      openCropModalForFile(file, fieldName);
    } catch (error) {
      toast.error(error?.message || "Failed to open image editor.");
    }
  };

  const handleDropUpload = (e, fieldName) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    const isCollectionsField =
      fieldName === "collectionsPhotoUrl" || fieldName.startsWith("collectionsTile:");
    if (isCollectionsField && file.type?.startsWith("video/")) {
      uploadCategoryFile(file, fieldName);
      return;
    }
    openCropModalForFile(file, fieldName);
  };

  const handlePickUpload = (e, fieldName) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const isCollectionsField =
      fieldName === "collectionsPhotoUrl" || fieldName.startsWith("collectionsTile:");
    if (isCollectionsField && file.type?.startsWith("video/")) {
      uploadCategoryFile(file, fieldName);
      return;
    }
    openCropModalForFile(file, fieldName);
  };

  const handleDeleteCategory = async (id) => {
    try {
      await axios.delete(`/store/categories/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCategories((prev) => prev.filter((c) => c._id !== id));
      toast.success("Category removed.");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to remove category.");
    }
  };

  const startEditCategory = (category) => {
    setEditingCategoryId(category._id);
    const legacy = String(category.imageUrl || "").trim();
    setCategoryForm({
      label: category.label || "",
      arLabel: category.arLabel || "",
      slug: category.slug || "",
      parentId: category.parentId ? String(category.parentId) : "",
      isActive: category.isActive !== false,
      iconUrl: category.iconUrl || "",
      coverImageUrl: String(category.coverImageUrl || "").trim() || legacy,
      storeHomeImageUrl: String(category.storeHomeImageUrl || "").trim() || legacy,
      seoTitle: category.seoTitle || "",
      seoDescription: category.seoDescription || "",
      serialGroupCode: String(category.serialGroupCode || "").toUpperCase(),
    });
  };

  const categoriesTree = useMemo(() => buildTreeFromFlat(categories), [categories]);
  const categoriesFlatWithDepth = useMemo(() => flattenTree(categoriesTree), [categoriesTree]);

  useEffect(() => {
    // expand root nodes by default
    const next = new Set();
    categoriesTree.forEach((n) => next.add(String(n._id)));
    setAdminCatsOpen(next);
  }, [categoriesTree]);

  const toggleAdminCatOpen = useCallback((id) => {
    setAdminCatsOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleCategoryActive = useCallback(
    async (category) => {
      try {
        const nextActive = !(category?.isActive === false);
        const { data } = await axios.put(
          `/store/categories/${category._id}`,
          { isActive: !nextActive },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setCategories((prev) => prev.map((c) => (c._id === category._id ? data : c)));
        toast.success(!nextActive ? "Category activated." : "Category deactivated.");
      } catch (error) {
        toast.error(error?.response?.data?.message || "Failed to toggle category.");
      }
    },
    [token]
  );

  const saveCategoryOrder = async (nextCategories) => {
    try {
      const payload = {
        items: nextCategories.map((cat, index) => ({
          id: cat._id,
          sortOrder: index,
        })),
      };
      const { data } = await axios.put("/store/categories/reorder", payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const ordered = Array.isArray(data) ? data : [];
      setCategories(
        [...ordered].sort(
          (a, b) => Number(a?.sortOrder || 0) - Number(b?.sortOrder || 0)
        )
      );
      toast.success("Category order updated.");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to save order.");
    }
  };

  const handleCategoryDragStart = (id) => {
    setDraggedCategoryId(id);
  };

  const handleCategoryDrop = (targetId) => {
    if (!draggedCategoryId || draggedCategoryId === targetId) {
      setDraggedCategoryId(null);
      return;
    }
    const fromIndex = categories.findIndex((c) => c._id === draggedCategoryId);
    const toIndex = categories.findIndex((c) => c._id === targetId);
    if (fromIndex < 0 || toIndex < 0) {
      setDraggedCategoryId(null);
      return;
    }

    const next = [...categories];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    setCategories(next);
    setDraggedCategoryId(null);
    saveCategoryOrder(next);
  };

  const registerTilePreviewMediaSize = useCallback((tileId, el) => {
    if (!tileId || !el) return;
    const w = el.naturalWidth || el.videoWidth;
    const h = el.naturalHeight || el.videoHeight;
    if (!w || !h) return;
    setTilePreviewMediaSize((prev) => {
      const cur = prev[tileId];
      if (cur?.w === w && cur?.h === h) return prev;
      return { ...prev, [tileId]: { w, h } };
    });
  }, []);

  const handleTilePreviewClick = (e, tileId) => {
    const container = e.currentTarget;
    const rect = container.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const media = container.querySelector("img, video");
    const cached = tilePreviewMediaSize[tileId];
    const mediaW =
      media?.naturalWidth ||
      media?.videoWidth ||
      cached?.w ||
      rect.width;
    const mediaH =
      media?.naturalHeight ||
      media?.videoHeight ||
      cached?.h ||
      rect.height;
    const { x, y } = clientPointToMediaPercent(
      e.clientX,
      e.clientY,
      rect,
      mediaW,
      mediaH,
      "contain"
    );
    const tile = collectionsForm.tileMedia?.[tileId] || {};
    let hotspotsList =
      Array.isArray(tile.hotspots) && tile.hotspots.length > 0
        ? [...tile.hotspots]
        : [
            {
              localId: newHotspotLocalId(),
              hotspotX: 50,
              hotspotY: 50,
              productId: "",
            },
          ];
    const idx = Math.min(
      Math.max(0, Number(tile.activeHotspotIndex) || 0),
      hotspotsList.length - 1
    );
    const next = hotspotsList.map((h, i) =>
      i === idx
        ? {
            ...h,
            hotspotX: normalizePercent(x, 50),
            hotspotY: normalizePercent(y, 50),
          }
        : h
    );
    updateTileMedia(tileId, { hotspots: next, activeHotspotIndex: idx });
  };

  const handleSaveCollections = async () => {
    try {
      setSavingCollections(true);
      const result = await persistCollectionsFormSnapshot(collectionsForm, {
        skipContentCheck: false,
        successMessage: "Collections media and hotspots saved.",
      });
      if (!result.ok && result.reason === "empty") {
        toast.error("Please set at least one tile media or hotspot before saving.");
      } else if (!result.ok && result.error) {
        toast.error(
          result.error?.response?.data?.message || "Failed to save collections settings."
        );
      }
    } finally {
      setSavingCollections(false);
    }
  };

  const handleSaveHeroBackground = async () => {
    try {
      setSavingHeroBackground(true);
      const result = await persistCollectionsFormSnapshot(collectionsFormRef.current, {
        // Hero background can be saved even if tiles are empty.
        skipContentCheck: true,
        successMessage: "Hero background saved.",
      });
      if (!result.ok && result.error) {
        toast.error(
          result.error?.response?.data?.message || "Failed to save hero background."
        );
      }
    } finally {
      setSavingHeroBackground(false);
    }
  };

  const updateHeroSlideBg = useCallback((category, nextUrl, variant = "desktop") => {
    const cat = String(category || "").toLowerCase();
    setCollectionsForm((prev) => {
      const cur = normalizeHeroSlideFormValue(prev.heroSlides?.[cat]);
      const url = String(nextUrl || "").trim();
      const next =
        variant === "mobile"
          ? { ...cur, mobile: url }
          : variant === "desktopAr"
          ? { ...cur, desktopAr: url }
          : variant === "mobileAr"
          ? { ...cur, mobileAr: url }
          : variant === "productImage"
          ? { ...cur, productImage: url }
          : variant === "heroProductId"
          ? { ...cur, heroProductId: url }
          : { ...cur, desktop: url };
      return {
        ...prev,
        heroSlides: {
          ...(prev.heroSlides || {}),
          [cat]: next,
        },
      };
    });
  }, []);

  const selectHeroProduct = useCallback((category, productId) => {
    const cat = String(category || "").toLowerCase();
    setCollectionsForm((prev) => {
      const cur = normalizeHeroSlideFormValue(prev.heroSlides?.[cat]);
      return {
        ...prev,
        heroSlides: {
          ...(prev.heroSlides || {}),
          [cat]: { ...cur, heroProductId: String(productId || "").trim() },
        },
      };
    });
  }, []);

  const handleHeroSlideBgUpload = useCallback(
    async (e, category, variant = "desktop") => {
      const file = e?.target?.files?.[0];
      if (!file) return;
      try {
        const form = new FormData();
        form.append("media", file);
        const { data } = await axios.post("/store/products/collections-upload", form, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const uploadedUrl = data?.url || "";
        if (!uploadedUrl) {
          toast.error("Upload failed.");
          return;
        }
        updateHeroSlideBg(category, uploadedUrl, variant);
        const cat = String(category || "").toLowerCase();
        const prevHs = collectionsFormRef.current.heroSlides || {};
        const cur = normalizeHeroSlideFormValue(prevHs[cat]);
        const merged = {
          ...prevHs,
          [cat]:
            variant === "mobile"
              ? { ...cur, mobile: uploadedUrl }
              : variant === "desktopAr"
              ? { ...cur, desktopAr: uploadedUrl }
              : variant === "mobileAr"
              ? { ...cur, mobileAr: uploadedUrl }
              : variant === "productImage"
              ? { ...cur, productImage: uploadedUrl }
              : { ...cur, desktop: uploadedUrl },
        };
        const nextSnapshot = {
          ...collectionsFormRef.current,
          heroSlides: merged,
        };
        const result = await persistCollectionsFormSnapshot(nextSnapshot, {
          skipContentCheck: true,
          successMessage: "Hero background updated.",
        });
        if (!result.ok) {
          toast.info("Saved locally. Click “Save collections media” to persist if needed.");
        }
      } catch (error) {
        toast.error(error?.response?.data?.message || "Failed to upload hero background.");
      } finally {
        if (e?.target) e.target.value = "";
      }
    },
    [persistCollectionsFormSnapshot, token, updateHeroSlideBg]
  );

  const setHeroSlideProductPosition = useCallback((category, x, y) => {
    const cat = String(category || "").toLowerCase();
    setCollectionsForm((prev) => {
      const cur = normalizeHeroSlideFormValue(prev.heroSlides?.[cat]);
      return {
        ...prev,
        heroSlides: {
          ...(prev.heroSlides || {}),
          [cat]: {
            ...cur,
            heroProductX: Math.max(-80, Math.min(80, Math.round(Number(x) * 10) / 10 || 0)),
            heroProductY: Math.max(-80, Math.min(60, Math.round(Number(y) * 10) / 10 || 0)),
          },
        },
      };
    });
  }, []);

  const startHeroPosDrag = useCallback((e, cat, currentX, currentY, lang = "en") => {
    e.preventDefault();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    heroPosDragRef.current = { cat, sx: clientX, sy: clientY, ox: currentX, oy: currentY, lang };
    setHeroPosDragging(true);
  }, []);

  useEffect(() => {
    if (!heroPosDragging) return;
    const onMove = (e) => {
      const ds = heroPosDragRef.current;
      const preview = heroPosPreviewRef.current;
      if (!ds || !preview) return;
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const colW = preview.offsetWidth * 0.48;
      const colH = preview.offsetHeight;
      const dx = ((clientX - ds.sx) / colW) * 100;
      const dy = ((clientY - ds.sy) / colH) * 100;
      if (ds.lang === "ar") setHeroSlideProductPositionAr(ds.cat, ds.ox + dx, ds.oy + dy);
      else setHeroSlideProductPosition(ds.cat, ds.ox + dx, ds.oy + dy);
    };
    const onEnd = () => setHeroPosDragging(false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onEnd);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onEnd);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onEnd);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onEnd);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heroPosDragging, setHeroSlideProductPosition]);

  const setHeroProductScale = useCallback((category, scale) => {
    const cat = String(category || "").toLowerCase();
    setCollectionsForm((prev) => {
      const cur = normalizeHeroSlideFormValue(prev.heroSlides?.[cat]);
      return {
        ...prev,
        heroSlides: {
          ...(prev.heroSlides || {}),
          [cat]: { ...cur, heroProductScale: Math.max(0.1, Math.min(3, Math.round(Number(scale) * 10) / 10 || 1)) },
        },
      };
    });
  }, []);

  const setHeroSlideBtnPosition = useCallback((category, x, y) => {
    const cat = String(category || "").toLowerCase();
    setCollectionsForm((prev) => {
      const cur = normalizeHeroSlideFormValue(prev.heroSlides?.[cat]);
      return {
        ...prev,
        heroSlides: {
          ...(prev.heroSlides || {}),
          [cat]: {
            ...cur,
            heroBtnX: Math.max(-80, Math.min(80, Math.round(Number(x) * 10) / 10 || 0)),
            heroBtnY: Math.max(-80, Math.min(80, Math.round(Number(y) * 10) / 10 || 0)),
          },
        },
      };
    });
  }, []);

  const setHeroSlideBtnMobilePosition = useCallback((category, x, y) => {
    const cat = String(category || "").toLowerCase();
    setCollectionsForm((prev) => {
      const cur = normalizeHeroSlideFormValue(prev.heroSlides?.[cat]);
      return {
        ...prev,
        heroSlides: {
          ...(prev.heroSlides || {}),
          [cat]: {
            ...cur,
            heroBtnMobileX: Math.max(-80, Math.min(80, Math.round(Number(x) * 10) / 10 || 0)),
            heroBtnMobileY: Math.max(-80, Math.min(80, Math.round(Number(y) * 10) / 10 || 0)),
          },
        },
      };
    });
  }, []);

  const startHeroBtnDrag = useCallback((e, cat, currentX, currentY, lang = "en") => {
    e.preventDefault();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    heroBtnDragRef.current = { cat, sx: clientX, sy: clientY, ox: currentX, oy: currentY, lang };
    setHeroBtnDragging(true);
  }, []);

  useEffect(() => {
    if (!heroBtnDragging) return;
    const onMove = (e) => {
      const ds = heroBtnDragRef.current;
      const preview = heroBtnPreviewRef.current;
      if (!ds || !preview) return;
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const w = preview.offsetWidth * 0.52;
      const h = preview.offsetHeight;
      const dx = ((clientX - ds.sx) / w) * 100;
      const dy = ((clientY - ds.sy) / h) * 100;
      if (ds.lang === "ar") setHeroSlideBtnPositionAr(ds.cat, ds.ox + dx, ds.oy + dy);
      else setHeroSlideBtnPosition(ds.cat, ds.ox + dx, ds.oy + dy);
    };
    const onEnd = () => setHeroBtnDragging(false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onEnd);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onEnd);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onEnd);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onEnd);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heroBtnDragging, setHeroSlideBtnPosition]);

  const updateHeroSlideText = useCallback((category, field, value) => {
    const cat = String(category || "").toLowerCase();
    setCollectionsForm((prev) => {
      const cur = normalizeHeroSlideFormValue(prev.heroSlides?.[cat]);
      return {
        ...prev,
        heroSlides: { ...(prev.heroSlides || {}), [cat]: { ...cur, [field]: String(value) } },
      };
    });
  }, []);

  const setHeroProductMobileX = useCallback((category, x) => {
    const cat = String(category || "").toLowerCase();
    setCollectionsForm((prev) => {
      const cur = normalizeHeroSlideFormValue(prev.heroSlides?.[cat]);
      return {
        ...prev,
        heroSlides: {
          ...(prev.heroSlides || {}),
          [cat]: { ...cur, heroProductMobileX: Math.max(-80, Math.min(80, Math.round(Number(x) * 10) / 10 || 0)) },
        },
      };
    });
  }, []);

  const setHeroProductMobileY = useCallback((category, y) => {
    const cat = String(category || "").toLowerCase();
    setCollectionsForm((prev) => {
      const cur = normalizeHeroSlideFormValue(prev.heroSlides?.[cat]);
      return {
        ...prev,
        heroSlides: {
          ...(prev.heroSlides || {}),
          [cat]: { ...cur, heroProductMobileY: Math.max(-80, Math.min(80, Math.round(Number(y) * 10) / 10 || 0)) },
        },
      };
    });
  }, []);

  const setHeroProductMobileScale = useCallback((category, scale) => {
    const cat = String(category || "").toLowerCase();
    setCollectionsForm((prev) => {
      const cur = normalizeHeroSlideFormValue(prev.heroSlides?.[cat]);
      return {
        ...prev,
        heroSlides: {
          ...(prev.heroSlides || {}),
          [cat]: { ...cur, heroProductMobileScale: Math.max(0.1, Math.min(3, Math.round(Number(scale) * 10) / 10 || 1)) },
        },
      };
    });
  }, []);

  const setHeroSlideTextPosition = useCallback((category, x, y) => {
    const cat = String(category || "").toLowerCase();
    setCollectionsForm((prev) => {
      const cur = normalizeHeroSlideFormValue(prev.heroSlides?.[cat]);
      return {
        ...prev,
        heroSlides: {
          ...(prev.heroSlides || {}),
          [cat]: {
            ...cur,
            heroTextX: Math.max(-80, Math.min(80, Math.round(Number(x) * 10) / 10 || 0)),
            heroTextY: Math.max(-80, Math.min(80, Math.round(Number(y) * 10) / 10 || 0)),
          },
        },
      };
    });
  }, []);

  const setHeroDotsPosition = useCallback((category, x, y) => {
    const cat = String(category || "").toLowerCase();
    setCollectionsForm((prev) => {
      const cur = normalizeHeroSlideFormValue(prev.heroSlides?.[cat]);
      return {
        ...prev,
        heroSlides: {
          ...(prev.heroSlides || {}),
          [cat]: {
            ...cur,
            heroDotsX: Math.max(-50, Math.min(50, Math.round(Number(x) * 10) / 10 || 0)),
            heroDotsY: Math.max(0, Math.min(200, Math.round(Number(y)) || 0)),
          },
        },
      };
    });
  }, []);

  const setHeroMobileDotsPosition = useCallback((category, x, y) => {
    const cat = String(category || "").toLowerCase();
    setCollectionsForm((prev) => {
      const cur = normalizeHeroSlideFormValue(prev.heroSlides?.[cat]);
      return {
        ...prev,
        heroSlides: {
          ...(prev.heroSlides || {}),
          [cat]: {
            ...cur,
            heroDotsMobileX: Math.max(-50, Math.min(50, Math.round(Number(x) * 10) / 10 || 0)),
            heroDotsMobileY: Math.max(0, Math.min(200, Math.round(Number(y)) || 0)),
          },
        },
      };
    });
  }, []);

  const startHeroTextDrag = useCallback((e, cat, currentX, currentY, lang = "en") => {
    e.preventDefault();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    heroTextDragRef.current = { cat, sx: clientX, sy: clientY, ox: currentX, oy: currentY, lang };
    setHeroTextDragging(true);
  }, []);

  useEffect(() => {
    if (!heroTextDragging) return;
    const onMove = (e) => {
      const ds = heroTextDragRef.current;
      const preview = heroPosPreviewRef.current;
      if (!ds || !preview) return;
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const w = preview.offsetWidth * 0.52;
      const h = preview.offsetHeight;
      const dx = ((clientX - ds.sx) / w) * 100;
      const dy = ((clientY - ds.sy) / h) * 100;
      if (ds.lang === "ar") setHeroSlideTextPositionAr(ds.cat, ds.ox + dx, ds.oy + dy);
      else setHeroSlideTextPosition(ds.cat, ds.ox + dx, ds.oy + dy);
    };
    const onEnd = () => setHeroTextDragging(false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onEnd);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onEnd);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onEnd);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onEnd);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heroTextDragging, setHeroSlideTextPosition]);

  const updateHeroTrustItem = useCallback((category, index, field, value) => {
    const cat = String(category || "").toLowerCase();
    setCollectionsForm((prev) => {
      const cur = normalizeHeroSlideFormValue(prev.heroSlides?.[cat]);
      const items = normalizeTrustItems(cur.heroTrustItems);
      items[index] = { ...items[index], [field]: value };
      return {
        ...prev,
        heroSlides: {
          ...(prev.heroSlides || {}),
          [cat]: { ...cur, heroTrustItems: items },
        },
      };
    });
  }, []);

  const setHeroSlideProductPositionAr = useCallback((category, x, y) => {
    const cat = String(category || "").toLowerCase();
    setCollectionsForm((prev) => {
      const cur = normalizeHeroSlideFormValue(prev.heroSlides?.[cat]);
      return {
        ...prev,
        heroSlides: {
          ...(prev.heroSlides || {}),
          [cat]: {
            ...cur,
            heroProductXAr: Math.max(-80, Math.min(80, Math.round(Number(x) * 10) / 10 || 0)),
            heroProductYAr: Math.max(-80, Math.min(80, Math.round(Number(y) * 10) / 10 || 0)),
          },
        },
      };
    });
  }, []);

  const setHeroProductScaleAr = useCallback((category, scale) => {
    const cat = String(category || "").toLowerCase();
    setCollectionsForm((prev) => {
      const cur = normalizeHeroSlideFormValue(prev.heroSlides?.[cat]);
      return {
        ...prev,
        heroSlides: {
          ...(prev.heroSlides || {}),
          [cat]: { ...cur, heroProductScaleAr: Math.max(0.1, Math.min(3, Math.round(Number(scale) * 10) / 10 || 1)) },
        },
      };
    });
  }, []);

  const setHeroSlideBtnPositionAr = useCallback((category, x, y) => {
    const cat = String(category || "").toLowerCase();
    setCollectionsForm((prev) => {
      const cur = normalizeHeroSlideFormValue(prev.heroSlides?.[cat]);
      return {
        ...prev,
        heroSlides: {
          ...(prev.heroSlides || {}),
          [cat]: {
            ...cur,
            heroBtnXAr: Math.max(-80, Math.min(80, Math.round(Number(x) * 10) / 10 || 0)),
            heroBtnYAr: Math.max(-80, Math.min(80, Math.round(Number(y) * 10) / 10 || 0)),
          },
        },
      };
    });
  }, []);

  const setHeroSlideTextPositionAr = useCallback((category, x, y) => {
    const cat = String(category || "").toLowerCase();
    setCollectionsForm((prev) => {
      const cur = normalizeHeroSlideFormValue(prev.heroSlides?.[cat]);
      return {
        ...prev,
        heroSlides: {
          ...(prev.heroSlides || {}),
          [cat]: {
            ...cur,
            heroTextXAr: Math.max(-80, Math.min(80, Math.round(Number(x) * 10) / 10 || 0)),
            heroTextYAr: Math.max(-80, Math.min(80, Math.round(Number(y) * 10) / 10 || 0)),
          },
        },
      };
    });
  }, []);

  const setHeroProductMobileXAr = useCallback((category, x) => {
    const cat = String(category || "").toLowerCase();
    setCollectionsForm((prev) => {
      const cur = normalizeHeroSlideFormValue(prev.heroSlides?.[cat]);
      return {
        ...prev,
        heroSlides: {
          ...(prev.heroSlides || {}),
          [cat]: { ...cur, heroProductMobileXAr: Math.max(-80, Math.min(80, Math.round(Number(x) * 10) / 10 || 0)) },
        },
      };
    });
  }, []);

  const setHeroProductMobileYAr = useCallback((category, y) => {
    const cat = String(category || "").toLowerCase();
    setCollectionsForm((prev) => {
      const cur = normalizeHeroSlideFormValue(prev.heroSlides?.[cat]);
      return {
        ...prev,
        heroSlides: {
          ...(prev.heroSlides || {}),
          [cat]: { ...cur, heroProductMobileYAr: Math.max(-80, Math.min(80, Math.round(Number(y) * 10) / 10 || 0)) },
        },
      };
    });
  }, []);

  const setHeroProductMobileScaleAr = useCallback((category, scale) => {
    const cat = String(category || "").toLowerCase();
    setCollectionsForm((prev) => {
      const cur = normalizeHeroSlideFormValue(prev.heroSlides?.[cat]);
      return {
        ...prev,
        heroSlides: {
          ...(prev.heroSlides || {}),
          [cat]: { ...cur, heroProductMobileScaleAr: Math.max(0.1, Math.min(3, Math.round(Number(scale) * 10) / 10 || 1)) },
        },
      };
    });
  }, []);

  const setHeroSlideBtnMobilePositionAr = useCallback((category, x, y) => {
    const cat = String(category || "").toLowerCase();
    setCollectionsForm((prev) => {
      const cur = normalizeHeroSlideFormValue(prev.heroSlides?.[cat]);
      return {
        ...prev,
        heroSlides: {
          ...(prev.heroSlides || {}),
          [cat]: {
            ...cur,
            heroBtnMobileXAr: Math.max(-80, Math.min(80, Math.round(Number(x) * 10) / 10 || 0)),
            heroBtnMobileYAr: Math.max(-80, Math.min(80, Math.round(Number(y) * 10) / 10 || 0)),
          },
        },
      };
    });
  }, []);

  const addHeroProductItem = useCallback((category, productId) => {
    const cat = String(category || "").toLowerCase();
    const pid = String(productId || "").trim();
    if (!pid) return;
    setCollectionsForm((prev) => {
      const cur = normalizeHeroSlideFormValue(prev.heroSlides?.[cat]);
      const existing = Array.isArray(cur.heroProductItems) ? cur.heroProductItems : [];
      if (existing.some((it) => it.productId === pid) || existing.length >= 6) return prev;
      const newItem = normalizeHeroProductItemForm({ productId: pid });
      if (!newItem) return prev;
      return {
        ...prev,
        heroSlides: { ...(prev.heroSlides || {}), [cat]: { ...cur, heroProductItems: [...existing, newItem] } },
      };
    });
  }, []);

  const removeHeroProductItem = useCallback((category, productId) => {
    const cat = String(category || "").toLowerCase();
    const pid = String(productId || "").trim();
    setCollectionsForm((prev) => {
      const cur = normalizeHeroSlideFormValue(prev.heroSlides?.[cat]);
      const existing = Array.isArray(cur.heroProductItems) ? cur.heroProductItems : [];
      return {
        ...prev,
        heroSlides: { ...(prev.heroSlides || {}), [cat]: { ...cur, heroProductItems: existing.filter((it) => it.productId !== pid) } },
      };
    });
  }, []);

  const updateHeroProductItem = useCallback((category, productId, field, value) => {
    const cat = String(category || "").toLowerCase();
    const pid = String(productId || "").trim();
    setCollectionsForm((prev) => {
      const cur = normalizeHeroSlideFormValue(prev.heroSlides?.[cat]);
      const existing = Array.isArray(cur.heroProductItems) ? cur.heroProductItems : [];
      const updated = existing.map((it) => {
        if (it.productId !== pid) return it;
        return normalizeHeroProductItemForm({ ...it, [field]: value }) || it;
      });
      return {
        ...prev,
        heroSlides: { ...(prev.heroSlides || {}), [cat]: { ...cur, heroProductItems: updated } },
      };
    });
  }, []);

  const handleHeroProductItemImageUpload = useCallback(
    async (e, category, productId) => {
      const file = e?.target?.files?.[0];
      if (!file) return;
      try {
        const form = new FormData();
        form.append("media", file);
        const { data } = await axios.post("/store/products/collections-upload", form, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const uploadedUrl = data?.url || "";
        if (!uploadedUrl) { toast.error("Upload failed."); return; }
        updateHeroProductItem(category, productId, "imageUrl", uploadedUrl);
      } catch (error) {
        toast.error(error?.response?.data?.message || "Failed to upload product image.");
      } finally {
        if (e?.target) e.target.value = "";
      }
    },
    [token, updateHeroProductItem]
  );

  return (
    <main className="store-control-page">
      <header className="store-control-page-intro">
        <h1>Store Control</h1>
        <p>
          Expand a card to edit that area. Hero products, collection tiles, and categories are
          managed separately.
        </p>
      </header>

      <div
        className={`store-control-panels${openSection ? "" : " store-control-panels--all-closed"}`}
      >
        <StoreControlPanel
          sectionId="backup"
          isOpen={openSection === "backup"}
          onToggle={toggleStoreSection}
          title="Backup & import (Store)"
          description="Download a JSON backup of all store data, or import a backup to restore it (replaces current store data)."
          badge="JSON"
        >
          <div className="store-control-panel-inner">
            <div className="store-backup-panel">
              <ol className="store-backup-steps" aria-label="Backup workflow">
                <li>
                  <span className="store-backup-steps__num">1</span>
                  <span>
                    <strong>Download</strong> a backup before risky changes.
                  </span>
                </li>
                <li>
                  <span className="store-backup-steps__num">2</span>
                  <span>
                    <strong>Choose</strong> a JSON file to import (same format).
                  </span>
                </li>
                <li>
                  <span className="store-backup-steps__num">3</span>
                  <span>
                    <strong>Apply</strong> to overwrite current store data.
                  </span>
                </li>
              </ol>

              <div className="store-backup-actions">
                <button
                  type="button"
                  className="store-backup-btn store-backup-btn--export"
                  onClick={downloadStoreBundle}
                  disabled={bundleExportLoading || bundleImportLoading || bundleApplyLoading}
                >
                  {bundleExportLoading ? "Exporting…" : "Download JSON backup"}
                </button>

                <input
                  ref={storeBundleInputRef}
                  type="file"
                  accept="application/json"
                  className="store-backup-file-input"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = "";
                    void onPickStoreBundleFile(f);
                  }}
                  disabled={bundleExportLoading || bundleImportLoading || bundleApplyLoading}
                />

                <button
                  type="button"
                  className="store-backup-btn store-backup-btn--import"
                  onClick={() => storeBundleInputRef.current?.click()}
                  disabled={bundleExportLoading || bundleImportLoading || bundleApplyLoading}
                >
                  {bundleImportLoading ? "Loading…" : "Choose backup file"}
                </button>

                <button
                  type="button"
                  className="store-backup-btn store-backup-btn--apply"
                  onClick={applyStoreBundle}
                  disabled={!pendingStoreBundle || bundleExportLoading || bundleImportLoading || bundleApplyLoading}
                >
                  {bundleApplyLoading ? "Applying…" : "Apply imported data"}
                </button>

                {pendingStoreBundle ? (
                  <button
                    type="button"
                    className="store-backup-btn store-backup-btn--ghost"
                    onClick={() => setPendingStoreBundle(null)}
                    disabled={bundleApplyLoading}
                  >
                    Cancel
                  </button>
                ) : null}
              </div>

              {pendingStoreBundle ? (
                <div className="store-import-staged" aria-label="Staged import">
                  <div className="store-import-staged__body">
                    <strong>Import staged:</strong>{" "}
                    {Array.isArray(pendingStoreBundle?.store?.categories)
                      ? pendingStoreBundle.store.categories.length
                      : 0}{" "}
                    categories,{" "}
                    {Array.isArray(pendingStoreBundle?.store?.products)
                      ? pendingStoreBundle.store.products.length
                      : 0}{" "}
                    products,{" "}
                    {Array.isArray(pendingStoreBundle?.store?.collectionsConfigs)
                      ? pendingStoreBundle.store.collectionsConfigs.length
                      : 0}{" "}
                    configs.
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </StoreControlPanel>

        <StoreControlPanel
          sectionId="hero"
          isOpen={openSection === "hero"}
          onToggle={toggleStoreSection}
          title="Hero section product control"
          description="Choose which product appears in the storefront hero and upload a custom no-background photo."
          badge={loading ? "Loading…" : "Hero product"}
        >
          <div className="store-control-panel-inner">
            <div className="store-control-panel-actions">
              <button
                type="button"
                className="store-control-panel-actions-primary"
                onClick={handleSaveHeroBackground}
                disabled={savingHeroBackground || loading}
              >
                {savingHeroBackground ? "Saving…" : "Save hero settings"}
              </button>
            </div>
            {loading ? (
              <div className="store-control-loading">Loading store control...</div>
            ) : (
              <>
                {["default"].map((cat) => {
                  const slide = normalizeHeroSlideFormValue(collectionsForm.heroSlides?.[cat]);
                  const selectedItems = Array.isArray(slide.heroProductItems) ? slide.heroProductItems : [];
                  const selectedIds = selectedItems.map((it) => it.productId);
                  const selectedProduct = selectedItems[0]?.productId ? rows.find((r) => String(r._id) === selectedItems[0].productId) : null;
                  const search = heroProductSearch.trim().toLowerCase();
                  const filteredRows = rows.filter((r) => {
                    if (!search) return true;
                    return (
                      (r.name || "").toLowerCase().includes(search) ||
                      (r.arName || "").toLowerCase().includes(search) ||
                      (r.category || "").toLowerCase().includes(search)
                    );
                  });
                  return (
                    <div key={cat} className="hero-picker">
                      {/* ── Featured products list ── */}
                      <div className="hero-picker-section">
                        <div className="hero-picker-section-title">Featured products</div>
                        <div className="hero-picker-section-desc">
                          Add up to 6 products — they cycle smoothly in the hero every 3.5s.
                        </div>

                        {selectedItems.length > 0 && (
                          <div className="hero-picker-selected-list">
                            {selectedItems.map((item) => {
                              const pid = item.productId;
                              const p = rows.find((r) => String(r._id) === pid);
                              if (!p) return null;
                              const isExpanded = expandedHeroProductId === pid;
                              const isAr = heroEditorLang === "ar";
                              const itemX = isAr ? (item.xAr ?? 0) : (item.x ?? 0);
                              const itemY = isAr ? (item.yAr ?? 0) : (item.y ?? 0);
                              const itemScale = isAr ? (item.scaleAr ?? 1) : (item.scale ?? 1);
                              const itemMX = isAr ? (item.mobileXAr ?? 0) : (item.mobileX ?? 0);
                              const itemMY = isAr ? (item.mobileYAr ?? 0) : (item.mobileY ?? 0);
                              const itemMScale = isAr ? (item.mobileScaleAr ?? 1) : (item.mobileScale ?? 1);
                              const xKey = isAr ? "xAr" : "x";
                              const yKey = isAr ? "yAr" : "y";
                              const scaleKey = isAr ? "scaleAr" : "scale";
                              const mxKey = isAr ? "mobileXAr" : "mobileX";
                              const myKey = isAr ? "mobileYAr" : "mobileY";
                              const mscaleKey = isAr ? "mobileScaleAr" : "mobileScale";
                              return (
                                <div key={pid} className="hero-picker-item-wrap">
                                  <div className={`hero-picker-current${isExpanded ? " hero-picker-current--expanded" : ""}`}>
                                    {(item.imageUrl || p.thumbnailUrl) && (
                                      <img src={item.imageUrl || p.thumbnailUrl} alt="" className="hero-picker-current-thumb" />
                                    )}
                                    <div className="hero-picker-current-info">
                                      <span className="hero-picker-current-name">{p.arName || p.name}</span>
                                      <span className="hero-picker-current-cat">{p.category}</span>
                                    </div>
                                    <button
                                      type="button"
                                      className={`hero-picker-current-settings${isExpanded ? " hero-picker-current-settings--active" : ""}`}
                                      onClick={() => setExpandedHeroProductId((prev) => prev === pid ? null : pid)}
                                      title="Settings"
                                    >
                                      <i className={`fa-solid fa-${isExpanded ? "chevron-up" : "sliders"}`} />
                                    </button>
                                    <button
                                      type="button"
                                      className="hero-picker-current-remove"
                                      onClick={() => {
                                        removeHeroProductItem(cat, pid);
                                        if (expandedHeroProductId === pid) setExpandedHeroProductId(null);
                                      }}
                                      title="Remove"
                                    >
                                      <i className="fa-solid fa-xmark" />
                                    </button>
                                  </div>

                                  {isExpanded && (
                                    <div className="hero-picker-item-settings">
                                      <div className="hero-picker-item-settings-row">
                                        <span className="hero-picker-item-settings-label"><i className="fa-solid fa-image" /> No-bg image</span>
                                        <div className="hero-picker-upload-btns">
                                          <label className="hero-picker-upload-btn">
                                            <input type="file" accept="image/*" onChange={(e) => void handleHeroProductItemImageUpload(e, cat, pid)} />
                                            <i className="fa-solid fa-arrow-up-from-bracket" /> Upload
                                          </label>
                                          {item.imageUrl && (
                                            <button type="button" className="hero-picker-remove-btn" onClick={() => updateHeroProductItem(cat, pid, "imageUrl", "")}>
                                              <i className="fa-solid fa-trash" /> Remove
                                            </button>
                                          )}
                                        </div>
                                        {item.imageUrl && <img src={item.imageUrl} alt="" className="hero-picker-item-img-preview" />}
                                      </div>

                                      <div className="hero-picker-item-settings-group-label"><i className="fa-solid fa-desktop" /> Desktop {isAr ? "(AR)" : "(EN)"}</div>
                                      <div className="hero-pos-editor-axis">
                                        <span className="hero-pos-editor-axis-label">X <strong>{Math.round(itemX)}%</strong></span>
                                        <div className="hero-pos-editor-axis-btns">
                                          {[-10,-3,-1].map((d) => <button key={d} type="button" onClick={() => updateHeroProductItem(cat, pid, xKey, Math.round((itemX+d)*10)/10)}>{d}</button>)}
                                          <button type="button" className="hero-pos-editor-axis-reset" onClick={() => updateHeroProductItem(cat, pid, xKey, 0)}>⌀</button>
                                          {[1,3,10].map((d) => <button key={d} type="button" onClick={() => updateHeroProductItem(cat, pid, xKey, Math.round((itemX+d)*10)/10)}>+{d}</button>)}
                                        </div>
                                      </div>
                                      <div className="hero-pos-editor-axis">
                                        <span className="hero-pos-editor-axis-label">Y <strong>{Math.round(itemY)}%</strong></span>
                                        <div className="hero-pos-editor-axis-btns">
                                          {[-10,-3,-1].map((d) => <button key={d} type="button" onClick={() => updateHeroProductItem(cat, pid, yKey, Math.round((itemY+d)*10)/10)}>{d}</button>)}
                                          <button type="button" className="hero-pos-editor-axis-reset" onClick={() => updateHeroProductItem(cat, pid, yKey, 0)}>⌀</button>
                                          {[1,3,10].map((d) => <button key={d} type="button" onClick={() => updateHeroProductItem(cat, pid, yKey, Math.round((itemY+d)*10)/10)}>+{d}</button>)}
                                        </div>
                                      </div>
                                      <div className="hero-pos-editor-axis">
                                        <span className="hero-pos-editor-axis-label">Scale <strong>{Number(itemScale).toFixed(1)}×</strong></span>
                                        <div className="hero-pos-editor-axis-btns">
                                          {[-0.3,-0.1].map((d) => <button key={d} type="button" onClick={() => updateHeroProductItem(cat, pid, scaleKey, Math.max(0.1, Math.round((itemScale+d)*10)/10))}>{d}</button>)}
                                          <button type="button" className="hero-pos-editor-axis-reset" onClick={() => updateHeroProductItem(cat, pid, scaleKey, 1)}>⌀</button>
                                          {[0.1,0.3].map((d) => <button key={d} type="button" onClick={() => updateHeroProductItem(cat, pid, scaleKey, Math.max(0.1, Math.round((itemScale+d)*10)/10))}>+{d}</button>)}
                                        </div>
                                      </div>

                                      <div className="hero-picker-item-settings-group-label"><i className="fa-solid fa-mobile-screen" /> Mobile {isAr ? "(AR)" : "(EN)"}</div>
                                      <div className="hero-pos-editor-axis">
                                        <span className="hero-pos-editor-axis-label">X <strong>{Math.round(itemMX)}%</strong></span>
                                        <div className="hero-pos-editor-axis-btns">
                                          {[-10,-3,-1].map((d) => <button key={d} type="button" onClick={() => updateHeroProductItem(cat, pid, mxKey, Math.round((itemMX+d)*10)/10)}>{d}</button>)}
                                          <button type="button" className="hero-pos-editor-axis-reset" onClick={() => updateHeroProductItem(cat, pid, mxKey, 0)}>⌀</button>
                                          {[1,3,10].map((d) => <button key={d} type="button" onClick={() => updateHeroProductItem(cat, pid, mxKey, Math.round((itemMX+d)*10)/10)}>+{d}</button>)}
                                        </div>
                                      </div>
                                      <div className="hero-pos-editor-axis">
                                        <span className="hero-pos-editor-axis-label">Y <strong>{Math.round(itemMY)}%</strong></span>
                                        <div className="hero-pos-editor-axis-btns">
                                          {[-10,-3,-1].map((d) => <button key={d} type="button" onClick={() => updateHeroProductItem(cat, pid, myKey, Math.round((itemMY+d)*10)/10)}>{d}</button>)}
                                          <button type="button" className="hero-pos-editor-axis-reset" onClick={() => updateHeroProductItem(cat, pid, myKey, 0)}>⌀</button>
                                          {[1,3,10].map((d) => <button key={d} type="button" onClick={() => updateHeroProductItem(cat, pid, myKey, Math.round((itemMY+d)*10)/10)}>+{d}</button>)}
                                        </div>
                                      </div>
                                      <div className="hero-pos-editor-axis">
                                        <span className="hero-pos-editor-axis-label">Scale <strong>{Number(itemMScale).toFixed(1)}×</strong></span>
                                        <div className="hero-pos-editor-axis-btns">
                                          {[-0.3,-0.1].map((d) => <button key={d} type="button" onClick={() => updateHeroProductItem(cat, pid, mscaleKey, Math.max(0.1, Math.round((itemMScale+d)*10)/10))}>{d}</button>)}
                                          <button type="button" className="hero-pos-editor-axis-reset" onClick={() => updateHeroProductItem(cat, pid, mscaleKey, 1)}>⌀</button>
                                          {[0.1,0.3].map((d) => <button key={d} type="button" onClick={() => updateHeroProductItem(cat, pid, mscaleKey, Math.max(0.1, Math.round((itemMScale+d)*10)/10))}>+{d}</button>)}
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {selectedIds.length < 6 && (
                          <button
                            type="button"
                            className="hero-picker-add-btn"
                            onClick={() => setShowHeroProductPicker((v) => !v)}
                          >
                            <i className="fa-solid fa-plus" /> {showHeroProductPicker ? "Close picker" : "Add product"}
                          </button>
                        )}

                        {showHeroProductPicker && (
                          <>
                            <div className="hero-picker-search-row">
                              <input
                                type="search"
                                className="hero-picker-search"
                                placeholder="Search by name or category…"
                                value={heroProductSearch}
                                onChange={(e) => setHeroProductSearch(e.target.value)}
                              />
                            </div>
                            <div className="hero-picker-grid">
                              {filteredRows.length === 0 ? (
                                <div className="hero-picker-empty">No products found.</div>
                              ) : (
                                filteredRows.map((product) => {
                                  const isSelected = selectedIds.includes(String(product._id));
                                  return (
                                    <button
                                      key={product._id}
                                      type="button"
                                      className={`hero-picker-card${isSelected ? " hero-picker-card--selected" : ""}`}
                                      onClick={() => {
                                        if (isSelected) removeHeroProductItem(cat, String(product._id));
                                        else addHeroProductItem(cat, String(product._id));
                                      }}
                                      title={product.name}
                                    >
                                      <div className="hero-picker-card-img-wrap">
                                        {product.thumbnailUrl ? (
                                          <img src={product.thumbnailUrl} alt="" className="hero-picker-card-img" />
                                        ) : (
                                          <div className="hero-picker-card-no-img">
                                            <i className="fa-regular fa-image" />
                                          </div>
                                        )}
                                        {isSelected && (
                                          <span className="hero-picker-card-check">
                                            <i className="fa-solid fa-circle-check" />
                                          </span>
                                        )}
                                      </div>
                                      <span className="hero-picker-card-name">
                                        {product.arName || product.name}
                                      </span>
                                      <span className="hero-picker-card-cat">{product.category}</span>
                                    </button>
                                  );
                                })
                              )}
                            </div>
                          </>
                        )}
                      </div>

                      {/* ── Hero background image (buttons only, no preview) ── */}
                      <div className="hero-picker-section">
                        <div className="hero-picker-section-title">Background image — English (LTR)</div>
                        <div className="hero-picker-bg-btns-row">
                          <div className="hero-picker-bg-slot">
                            <span className="hero-picker-bg-slot-label">
                              <i className="fa-solid fa-desktop" /> Desktop
                              {slide.desktop && <span className="hero-picker-bg-slot-dot" />}
                            </span>
                            <div className="hero-picker-upload-btns">
                              <label className="hero-picker-upload-btn">
                                <input type="file" accept="image/*" onChange={(e) => void handleHeroSlideBgUpload(e, cat, "desktop")} />
                                <i className="fa-solid fa-arrow-up-from-bracket" /> Upload
                              </label>
                              {slide.desktop && (
                                <button type="button" className="hero-picker-remove-btn" onClick={() => updateHeroSlideBg(cat, "", "desktop")}>
                                  <i className="fa-solid fa-trash" /> Remove
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="hero-picker-bg-slot">
                            <span className="hero-picker-bg-slot-label">
                              <i className="fa-solid fa-mobile-screen" /> Mobile
                              {slide.mobile && <span className="hero-picker-bg-slot-dot" />}
                              <span className="hero-picker-bg-slot-opt">(optional)</span>
                            </span>
                            <div className="hero-picker-upload-btns">
                              <label className="hero-picker-upload-btn">
                                <input type="file" accept="image/*" onChange={(e) => void handleHeroSlideBgUpload(e, cat, "mobile")} />
                                <i className="fa-solid fa-arrow-up-from-bracket" /> Upload
                              </label>
                              {slide.mobile && (
                                <button type="button" className="hero-picker-remove-btn" onClick={() => updateHeroSlideBg(cat, "", "mobile")}>
                                  <i className="fa-solid fa-trash" /> Remove
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* ── Arabic background image ── */}
                      <div className="hero-picker-section">
                        <div className="hero-picker-section-title">
                          Background image — Arabic (RTL)
                          <span className="hero-picker-section-opt"> (optional — uses English bg if not set)</span>
                        </div>
                        <div className="hero-picker-bg-btns-row">
                          <div className="hero-picker-bg-slot">
                            <span className="hero-picker-bg-slot-label">
                              <i className="fa-solid fa-desktop" /> Desktop AR
                              {slide.desktopAr && <span className="hero-picker-bg-slot-dot" />}
                            </span>
                            <div className="hero-picker-upload-btns">
                              <label className="hero-picker-upload-btn">
                                <input type="file" accept="image/*" onChange={(e) => void handleHeroSlideBgUpload(e, cat, "desktopAr")} />
                                <i className="fa-solid fa-arrow-up-from-bracket" /> Upload
                              </label>
                              {slide.desktopAr && (
                                <button type="button" className="hero-picker-remove-btn" onClick={() => updateHeroSlideBg(cat, "", "desktopAr")}>
                                  <i className="fa-solid fa-trash" /> Remove
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="hero-picker-bg-slot">
                            <span className="hero-picker-bg-slot-label">
                              <i className="fa-solid fa-mobile-screen" /> Mobile AR
                              {slide.mobileAr && <span className="hero-picker-bg-slot-dot" />}
                              <span className="hero-picker-bg-slot-opt">(optional)</span>
                            </span>
                            <div className="hero-picker-upload-btns">
                              <label className="hero-picker-upload-btn">
                                <input type="file" accept="image/*" onChange={(e) => void handleHeroSlideBgUpload(e, cat, "mobileAr")} />
                                <i className="fa-solid fa-arrow-up-from-bracket" /> Upload
                              </label>
                              {slide.mobileAr && (
                                <button type="button" className="hero-picker-remove-btn" onClick={() => updateHeroSlideBg(cat, "", "mobileAr")}>
                                  <i className="fa-solid fa-trash" /> Remove
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* ── Combined desktop editor: product + buttons + text in one view ── */}
                      <div className="hero-picker-section">
                        <div className="hero-picker-section-title">Desktop layout editor</div>
                        {/* EN / AR language switch */}
                        <div className="hero-editor-lang-switch">
                          <button
                            type="button"
                            className={`hero-editor-lang-btn${heroEditorLang === "en" ? " hero-editor-lang-btn--active" : ""}`}
                            onClick={() => setHeroEditorLang("en")}
                          >EN</button>
                          <button
                            type="button"
                            className={`hero-editor-lang-btn${heroEditorLang === "ar" ? " hero-editor-lang-btn--active" : ""}`}
                            onClick={() => setHeroEditorLang("ar")}
                          >AR</button>
                        </div>
                        {/* Text editor — EN + AR for title and subtitle */}
                        <div className="hero-text-editor">
                          <div className="hero-text-editor-row">
                            <span className="hero-text-editor-label">Title EN</span>
                            <input
                              type="text"
                              className="hero-text-editor-input"
                              value={slide.heroTitleEn || ""}
                              placeholder="Handcrafted Wood. Timeless Design."
                              onChange={(e) => updateHeroSlideText(cat, "heroTitleEn", e.target.value)}
                            />
                          </div>
                          <div className="hero-text-editor-row">
                            <span className="hero-text-editor-label">Title AR</span>
                            <input
                              type="text"
                              className="hero-text-editor-input"
                              dir="rtl"
                              value={slide.heroTitleAr || ""}
                              placeholder="صناعة خشبية بروح فنية."
                              onChange={(e) => updateHeroSlideText(cat, "heroTitleAr", e.target.value)}
                            />
                          </div>
                          <div className="hero-text-editor-row">
                            <span className="hero-text-editor-label">Subtitle EN</span>
                            <input
                              type="text"
                              className="hero-text-editor-input"
                              value={slide.heroSubtitleEn || ""}
                              placeholder="Premium furniture crafted for homes and hospitality."
                              onChange={(e) => updateHeroSlideText(cat, "heroSubtitleEn", e.target.value)}
                            />
                          </div>
                          <div className="hero-text-editor-row">
                            <span className="hero-text-editor-label">Subtitle AR</span>
                            <input
                              type="text"
                              className="hero-text-editor-input"
                              dir="rtl"
                              value={slide.heroSubtitleAr || ""}
                              placeholder="أثاث فاخر مصنوع للمنازل ومشاريع الضيافة."
                              onChange={(e) => updateHeroSlideText(cat, "heroSubtitleAr", e.target.value)}
                            />
                          </div>
                        </div>
                        <div className="hero-picker-section-desc" style={{marginTop:10}}>
                          Drag the product image or the buttons to reposition them.
                          {heroEditorLang === "ar" && <span className="hero-picker-section-opt"> — editing Arabic positions</span>}
                        </div>
                        {/* Resolve current-lang positions for preview */}
                        {(() => {
                          const isAr = heroEditorLang === "ar";
                          const previewBg = isAr ? (slide.desktopAr || slide.desktop) : slide.desktop;
                          const prodX = isAr ? (slide.heroProductXAr || 0) : (slide.heroProductX || 0);
                          const prodY = isAr ? (slide.heroProductYAr || 0) : (slide.heroProductY || 0);
                          const prodScale = isAr ? (slide.heroProductScaleAr || 1) : (slide.heroProductScale || 1);
                          const txtX = isAr ? (slide.heroTextXAr || 0) : (slide.heroTextX || 0);
                          const txtY = isAr ? (slide.heroTextYAr || 0) : (slide.heroTextY || 0);
                          const btnX = isAr ? (slide.heroBtnXAr || 0) : (slide.heroBtnX || 0);
                          const btnY = isAr ? (slide.heroBtnYAr || 0) : (slide.heroBtnY || 0);
                          const dotsX = slide.heroDotsX || 0;
                          const dotsY = slide.heroDotsY != null ? slide.heroDotsY : 16;
                          const mockTitle = isAr ? (slide.heroTitleAr || "صناعة خشبية بروح فنية.") : (slide.heroTitleEn || "Handcrafted Wood. Timeless Design.");
                          const mockSubtitle = isAr ? (slide.heroSubtitleAr || "أثاث فاخر للمنازل.") : (slide.heroSubtitleEn || "Premium furniture crafted for homes.");
                          const setPos = isAr ? setHeroSlideProductPositionAr : setHeroSlideProductPosition;
                          const setTxt = isAr ? setHeroSlideTextPositionAr : setHeroSlideTextPosition;
                          const setBtn = isAr ? setHeroSlideBtnPositionAr : setHeroSlideBtnPosition;
                          const setScale = isAr ? setHeroProductScaleAr : setHeroProductScale;
                          return (
                            <>
                              <div
                                className={`hero-pos-editor${isAr ? " hero-pos-editor--rtl" : ""}${heroPosDragging || heroBtnDragging || heroTextDragging ? " hero-pos-editor--dragging" : ""}`}
                                ref={(el) => { heroPosPreviewRef.current = el; heroBtnPreviewRef.current = el; }}
                              >
                                {previewBg ? (
                                  <img src={previewBg} alt="" className="hero-pos-editor-bg" draggable="false" />
                                ) : (
                                  <div className="hero-pos-editor-bg-fallback" />
                                )}
                                <div className="hero-pos-editor-overlay" />
                                {/* Text panel */}
                                <div
                                  className="hero-pos-editor-text-panel"
                                  style={{ transform: `translate(${txtX}%, ${txtY}%)` }}
                                >
                                  <div
                                    className="hero-pos-editor-text-area"
                                    style={{ cursor: heroTextDragging ? "grabbing" : "grab" }}
                                    onMouseDown={(e) => startHeroTextDrag(e, cat, txtX, txtY, heroEditorLang)}
                                    onTouchStart={(e) => startHeroTextDrag(e, cat, txtX, txtY, heroEditorLang)}
                                  >
                                    <div className="hero-pos-editor-mock-title" dir={isAr ? "rtl" : "ltr"}>{mockTitle}</div>
                                    <div className="hero-pos-editor-mock-subtitle" dir={isAr ? "rtl" : "ltr"}>{mockSubtitle}</div>
                                  </div>
                                  <div
                                    className="hero-pos-editor-mock-btns"
                                    style={{ transform: `translate(${btnX}%, ${btnY}%)`, cursor: heroBtnDragging ? "grabbing" : "grab" }}
                                    onMouseDown={(e) => startHeroBtnDrag(e, cat, btnX, btnY, heroEditorLang)}
                                    onTouchStart={(e) => startHeroBtnDrag(e, cat, btnX, btnY, heroEditorLang)}
                                  >
                                    <div className="hero-pos-editor-mock-btn hero-pos-editor-mock-btn--labeled">Btn</div>
                                    <div className="hero-pos-editor-mock-btn hero-pos-editor-mock-btn--ghost hero-pos-editor-mock-btn--labeled">Btn</div>
                                  </div>
                                </div>
                                {/* Product col */}
                                <div className="hero-pos-editor-product-col">
                                  {(slide.productImage || selectedProduct?.thumbnailUrl) ? (
                                    <img
                                      src={slide.productImage || selectedProduct?.thumbnailUrl}
                                      alt=""
                                      className="hero-pos-editor-product-img"
                                      style={{
                                        transform: `translate(${prodX}%, ${prodY}%) scale(${prodScale})`,
                                        cursor: heroPosDragging ? "grabbing" : "grab",
                                      }}
                                      onMouseDown={(e) => startHeroPosDrag(e, cat, prodX, prodY, heroEditorLang)}
                                      onTouchStart={(e) => startHeroPosDrag(e, cat, prodX, prodY, heroEditorLang)}
                                      draggable="false"
                                    />
                                  ) : (
                                    <div className="hero-pos-editor-no-product">
                                      <i className="fa-regular fa-image" />
                                      <span>Select a product first</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              {/* All controls in one compact grid */}
                              <div className="hero-combined-controls">
                                <div className="hero-combined-group">
                                  <div className="hero-combined-group-label"><i className="fa-solid fa-image" /> Product</div>
                                  <div className="hero-pos-editor-axis">
                                    <span className="hero-pos-editor-axis-label">X <strong>{Math.round(prodX)}%</strong></span>
                                    <div className="hero-pos-editor-axis-btns">
                                      {[-10,-3,-1].map((d) => <button key={d} type="button" onClick={() => setPos(cat, prodX+d, prodY)}>{d}</button>)}
                                      <button type="button" className="hero-pos-editor-axis-reset" onClick={() => setPos(cat, 0, prodY)}>⌀</button>
                                      {[1,3,10].map((d) => <button key={d} type="button" onClick={() => setPos(cat, prodX+d, prodY)}>+{d}</button>)}
                                    </div>
                                  </div>
                                  <div className="hero-pos-editor-axis">
                                    <span className="hero-pos-editor-axis-label">Y <strong>{Math.round(prodY)}%</strong></span>
                                    <div className="hero-pos-editor-axis-btns">
                                      {[-10,-3,-1].map((d) => <button key={d} type="button" onClick={() => setPos(cat, prodX, prodY+d)}>{d}</button>)}
                                      <button type="button" className="hero-pos-editor-axis-reset" onClick={() => setPos(cat, prodX, 0)}>⌀</button>
                                      {[1,3,10].map((d) => <button key={d} type="button" onClick={() => setPos(cat, prodX, prodY+d)}>+{d}</button>)}
                                    </div>
                                  </div>
                                  <div className="hero-pos-editor-axis">
                                    <span className="hero-pos-editor-axis-label">Scale <strong>{prodScale.toFixed(1)}×</strong></span>
                                    <div className="hero-pos-editor-axis-btns">
                                      {[-0.3,-0.1].map((d) => <button key={d} type="button" onClick={() => setScale(cat, prodScale+d)}>{d}</button>)}
                                      <button type="button" className="hero-pos-editor-axis-reset" onClick={() => setScale(cat, 1)}>⌀</button>
                                      {[0.1,0.3].map((d) => <button key={d} type="button" onClick={() => setScale(cat, prodScale+d)}>+{d}</button>)}
                                    </div>
                                  </div>
                                </div>
                                <div className="hero-combined-group">
                                  <div className="hero-combined-group-label"><i className="fa-solid fa-font" /> Text</div>
                                  <div className="hero-pos-editor-axis">
                                    <span className="hero-pos-editor-axis-label">X <strong>{Math.round(txtX)}%</strong></span>
                                    <div className="hero-pos-editor-axis-btns">
                                      {[-10,-3,-1].map((d) => <button key={d} type="button" onClick={() => setTxt(cat, txtX+d, txtY)}>{d}</button>)}
                                      <button type="button" className="hero-pos-editor-axis-reset" onClick={() => setTxt(cat, 0, txtY)}>⌀</button>
                                      {[1,3,10].map((d) => <button key={d} type="button" onClick={() => setTxt(cat, txtX+d, txtY)}>+{d}</button>)}
                                    </div>
                                  </div>
                                  <div className="hero-pos-editor-axis">
                                    <span className="hero-pos-editor-axis-label">Y <strong>{Math.round(txtY)}%</strong></span>
                                    <div className="hero-pos-editor-axis-btns">
                                      {[-10,-3,-1].map((d) => <button key={d} type="button" onClick={() => setTxt(cat, txtX, txtY+d)}>{d}</button>)}
                                      <button type="button" className="hero-pos-editor-axis-reset" onClick={() => setTxt(cat, txtX, 0)}>⌀</button>
                                      {[1,3,10].map((d) => <button key={d} type="button" onClick={() => setTxt(cat, txtX, txtY+d)}>+{d}</button>)}
                                    </div>
                                  </div>
                                </div>
                                <div className="hero-combined-group">
                                  <div className="hero-combined-group-label"><i className="fa-solid fa-computer-mouse" /> Buttons</div>
                                  <div className="hero-pos-editor-axis">
                                    <span className="hero-pos-editor-axis-label">X <strong>{Math.round(btnX)}%</strong></span>
                                    <div className="hero-pos-editor-axis-btns">
                                      {[-10,-3,-1].map((d) => <button key={d} type="button" onClick={() => setBtn(cat, btnX+d, btnY)}>{d}</button>)}
                                      <button type="button" className="hero-pos-editor-axis-reset" onClick={() => setBtn(cat, 0, btnY)}>⌀</button>
                                      {[1,3,10].map((d) => <button key={d} type="button" onClick={() => setBtn(cat, btnX+d, btnY)}>+{d}</button>)}
                                    </div>
                                  </div>
                                  <div className="hero-pos-editor-axis">
                                    <span className="hero-pos-editor-axis-label">Y <strong>{Math.round(btnY)}%</strong></span>
                                    <div className="hero-pos-editor-axis-btns">
                                      {[-10,-3,-1].map((d) => <button key={d} type="button" onClick={() => setBtn(cat, btnX, btnY+d)}>{d}</button>)}
                                      <button type="button" className="hero-pos-editor-axis-reset" onClick={() => setBtn(cat, btnX, 0)}>⌀</button>
                                      {[1,3,10].map((d) => <button key={d} type="button" onClick={() => setBtn(cat, btnX, btnY+d)}>+{d}</button>)}
                                    </div>
                                  </div>
                                </div>
                                <div className="hero-combined-group">
                                  <div className="hero-combined-group-label"><i className="fa-solid fa-circle-dot" /> Dots indicator</div>
                                  <div className="hero-pos-editor-axis">
                                    <span className="hero-pos-editor-axis-label">X <strong>{Math.round(dotsX)}%</strong></span>
                                    <div className="hero-pos-editor-axis-btns">
                                      {[-10,-3,-1].map((d) => <button key={d} type="button" onClick={() => setHeroDotsPosition(cat, dotsX+d, dotsY)}>{d}</button>)}
                                      <button type="button" className="hero-pos-editor-axis-reset" onClick={() => setHeroDotsPosition(cat, 0, dotsY)}>⌀</button>
                                      {[1,3,10].map((d) => <button key={d} type="button" onClick={() => setHeroDotsPosition(cat, dotsX+d, dotsY)}>+{d}</button>)}
                                    </div>
                                  </div>
                                  <div className="hero-pos-editor-axis">
                                    <span className="hero-pos-editor-axis-label">Y <strong>{Math.round(dotsY)}px</strong></span>
                                    <div className="hero-pos-editor-axis-btns">
                                      {[-20,-5,-1].map((d) => <button key={d} type="button" onClick={() => setHeroDotsPosition(cat, dotsX, dotsY+d)}>{d}</button>)}
                                      <button type="button" className="hero-pos-editor-axis-reset" onClick={() => setHeroDotsPosition(cat, dotsX, 16)}>⌀</button>
                                      {[1,5,20].map((d) => <button key={d} type="button" onClick={() => setHeroDotsPosition(cat, dotsX, dotsY+d)}>+{d}</button>)}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </>
                          );
                        })()}
                      </div>

                      {/* ── Mobile iPhone preview ── */}
                      <div className="hero-picker-section">
                        <div className="hero-picker-section-title">Mobile preview</div>
                        <div className="hero-picker-section-desc">
                          What the user sees on mobile. Adjust button position below.
                          {heroEditorLang === "ar" && <span className="hero-picker-section-opt"> — editing Arabic positions</span>}
                        </div>
                        {(() => {
                          const isAr = heroEditorLang === "ar";
                          const mobileBg = isAr
                            ? (slide.mobileAr || slide.desktopAr || slide.mobile || slide.desktop)
                            : (slide.mobile || slide.desktop);
                          const mProdX = isAr ? (slide.heroProductMobileXAr || 0) : (slide.heroProductMobileX || 0);
                          const mProdY = isAr ? (slide.heroProductMobileYAr || 0) : (slide.heroProductMobileY || 0);
                          const mProdScale = isAr ? (slide.heroProductMobileScaleAr || 1) : (slide.heroProductMobileScale || 1);
                          const mBtnX = isAr ? (slide.heroBtnMobileXAr || 0) : (slide.heroBtnMobileX || 0);
                          const mBtnY = isAr ? (slide.heroBtnMobileYAr || 0) : (slide.heroBtnMobileY || 0);
                          const setMProdX = isAr ? setHeroProductMobileXAr : setHeroProductMobileX;
                          const setMProdY = isAr ? setHeroProductMobileYAr : setHeroProductMobileY;
                          const setMProdScale = isAr ? setHeroProductMobileScaleAr : setHeroProductMobileScale;
                          const setMBtn = isAr ? setHeroSlideBtnMobilePositionAr : setHeroSlideBtnMobilePosition;
                          const firstItem = selectedItems[0];
                          const previewProductThumb = (firstItem?.imageUrl) ||
                            slide.productImage ||
                            (firstItem?.productId ? rows.find((r) => String(r._id) === firstItem.productId)?.thumbnailUrl : null);
                          return (
                            <div className="hero-mobile-editor">
                              <div className="hero-iphone-frame">
                                <div className="hero-iphone-notch" />
                                <div className="hero-iphone-screen">
                                  {mobileBg && (
                                    <img src={mobileBg} alt="" className="hero-iphone-bg" draggable="false" />
                                  )}
                                  <div className="hero-iphone-product-area">
                                    {previewProductThumb ? (
                                      <img
                                        src={previewProductThumb}
                                        alt=""
                                        className="hero-iphone-product-img"
                                        style={{ transform: `translate(${mProdX}%, ${mProdY}%) scale(${mProdScale})` }}
                                      />
                                    ) : (
                                      <div className="hero-iphone-no-product"><i className="fa-regular fa-image" /></div>
                                    )}
                                  </div>
                                  <div className="hero-iphone-btns-area" style={{ transform: `translate(${mBtnX}%, ${mBtnY}%)` }}>
                                    <div className="hero-iphone-btn hero-iphone-btn--primary">Shop Furniture</div>
                                    <div className="hero-iphone-btn hero-iphone-btn--secondary">Explore</div>
                                  </div>
                                </div>
                                <div className="hero-iphone-home-bar" />
                              </div>
                              <div className="hero-mobile-controls">
                                <div className="hero-combined-group-label"><i className="fa-solid fa-image" /> Product (mobile only)</div>
                                <div className="hero-pos-editor-axis">
                                  <span className="hero-pos-editor-axis-label">X <strong>{Math.round(mProdX)}%</strong></span>
                                  <div className="hero-pos-editor-axis-btns">
                                    {[-10,-3,-1].map((d) => <button key={d} type="button" onClick={() => setMProdX(cat, mProdX+d)}>{d}</button>)}
                                    <button type="button" className="hero-pos-editor-axis-reset" onClick={() => setMProdX(cat, 0)}>⌀</button>
                                    {[1,3,10].map((d) => <button key={d} type="button" onClick={() => setMProdX(cat, mProdX+d)}>+{d}</button>)}
                                  </div>
                                </div>
                                <div className="hero-pos-editor-axis">
                                  <span className="hero-pos-editor-axis-label">Y <strong>{Math.round(mProdY)}%</strong></span>
                                  <div className="hero-pos-editor-axis-btns">
                                    {[-10,-3,-1].map((d) => <button key={d} type="button" onClick={() => setMProdY(cat, mProdY+d)}>{d}</button>)}
                                    <button type="button" className="hero-pos-editor-axis-reset" onClick={() => setMProdY(cat, 0)}>⌀</button>
                                    {[1,3,10].map((d) => <button key={d} type="button" onClick={() => setMProdY(cat, mProdY+d)}>+{d}</button>)}
                                  </div>
                                </div>
                                <div className="hero-pos-editor-axis">
                                  <span className="hero-pos-editor-axis-label">Scale <strong>{mProdScale.toFixed(1)}×</strong></span>
                                  <div className="hero-pos-editor-axis-btns">
                                    {[-0.3,-0.1].map((d) => <button key={d} type="button" onClick={() => setMProdScale(cat, mProdScale+d)}>{d}</button>)}
                                    <button type="button" className="hero-pos-editor-axis-reset" onClick={() => setMProdScale(cat, 1)}>⌀</button>
                                    {[0.1,0.3].map((d) => <button key={d} type="button" onClick={() => setMProdScale(cat, mProdScale+d)}>+{d}</button>)}
                                  </div>
                                </div>
                                <div className="hero-combined-group-label" style={{marginTop:6}}><i className="fa-solid fa-mobile-screen" /> Buttons</div>
                                <div className="hero-pos-editor-axis">
                                  <span className="hero-pos-editor-axis-label">X <strong>{Math.round(mBtnX)}%</strong></span>
                                  <div className="hero-pos-editor-axis-btns">
                                    {[-10,-3,-1].map((d) => <button key={d} type="button" onClick={() => setMBtn(cat, mBtnX+d, mBtnY)}>{d}</button>)}
                                    <button type="button" className="hero-pos-editor-axis-reset" onClick={() => setMBtn(cat, 0, mBtnY)}>⌀</button>
                                    {[1,3,10].map((d) => <button key={d} type="button" onClick={() => setMBtn(cat, mBtnX+d, mBtnY)}>+{d}</button>)}
                                  </div>
                                </div>
                                <div className="hero-pos-editor-axis">
                                  <span className="hero-pos-editor-axis-label">Y <strong>{Math.round(mBtnY)}%</strong></span>
                                  <div className="hero-pos-editor-axis-btns">
                                    {[-10,-3,-1].map((d) => <button key={d} type="button" onClick={() => setMBtn(cat, mBtnX, mBtnY+d)}>{d}</button>)}
                                    <button type="button" className="hero-pos-editor-axis-reset" onClick={() => setMBtn(cat, mBtnX, 0)}>⌀</button>
                                    {[1,3,10].map((d) => <button key={d} type="button" onClick={() => setMBtn(cat, mBtnX, mBtnY+d)}>+{d}</button>)}
                                  </div>
                                </div>
                                <button type="button" className="hero-picker-remove-btn" onClick={() => setMBtn(cat, 0, 0)}>Reset buttons</button>
                                <div className="hero-combined-group-label" style={{marginTop:10}}><i className="fa-solid fa-circle-dot" /> Dots indicator (mobile)</div>
                                <div className="hero-pos-editor-axis">
                                  <span className="hero-pos-editor-axis-label">X <strong>{Math.round(slide.heroDotsMobileX || 0)}%</strong></span>
                                  <div className="hero-pos-editor-axis-btns">
                                    {[-10,-3,-1].map((d) => <button key={d} type="button" onClick={() => setHeroMobileDotsPosition(cat, (slide.heroDotsMobileX||0)+d, slide.heroDotsMobileY??10)}>{d}</button>)}
                                    <button type="button" className="hero-pos-editor-axis-reset" onClick={() => setHeroMobileDotsPosition(cat, 0, slide.heroDotsMobileY??10)}>⌀</button>
                                    {[1,3,10].map((d) => <button key={d} type="button" onClick={() => setHeroMobileDotsPosition(cat, (slide.heroDotsMobileX||0)+d, slide.heroDotsMobileY??10)}>+{d}</button>)}
                                  </div>
                                </div>
                                <div className="hero-pos-editor-axis">
                                  <span className="hero-pos-editor-axis-label">Y <strong>{Math.round(slide.heroDotsMobileY??10)}px</strong></span>
                                  <div className="hero-pos-editor-axis-btns">
                                    {[-20,-5,-1].map((d) => <button key={d} type="button" onClick={() => setHeroMobileDotsPosition(cat, slide.heroDotsMobileX||0, (slide.heroDotsMobileY??10)+d)}>{d}</button>)}
                                    <button type="button" className="hero-pos-editor-axis-reset" onClick={() => setHeroMobileDotsPosition(cat, slide.heroDotsMobileX||0, 10)}>⌀</button>
                                    {[1,5,20].map((d) => <button key={d} type="button" onClick={() => setHeroMobileDotsPosition(cat, slide.heroDotsMobileX||0, (slide.heroDotsMobileY??10)+d)}>+{d}</button>)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                      </div>

                      {/* ── Trust strip (compact) ── */}
                      <div className="hero-picker-section">
                        <div className="hero-picker-section-title">Trust strip</div>
                        <div className="hero-trust-editor-compact">
                          {(slide.heroTrustItems || DEFAULT_TRUST_ITEMS).slice(0, 3).map((item, idx) => (
                            <div key={idx} className="hero-trust-editor-compact-row">
                              <div className="hero-trust-editor-compact-icon">
                                {item.icon
                                  ? <i className={`fa-solid ${item.icon}`} />
                                  : <span className="hero-trust-editor-compact-num">#{idx+1}</span>
                                }
                                <input
                                  type="text"
                                  className="hero-trust-editor-compact-icon-input"
                                  value={item.icon || ""}
                                  placeholder="fa-award"
                                  title="FontAwesome icon class"
                                  onChange={(e) => updateHeroTrustItem(cat, idx, "icon", e.target.value)}
                                />
                              </div>
                              <input
                                type="text"
                                className="hero-trust-editor-compact-text"
                                value={item.en || ""}
                                placeholder="+15 Years"
                                onChange={(e) => updateHeroTrustItem(cat, idx, "en", e.target.value)}
                              />
                              <input
                                type="text"
                                className="hero-trust-editor-compact-text hero-trust-editor-compact-text--ar"
                                dir="rtl"
                                value={item.ar || ""}
                                placeholder="+١٥ سنة"
                                onChange={(e) => updateHeroTrustItem(cat, idx, "ar", e.target.value)}
                              />
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* ── Custom hero photo upload ── */}
                      <div className="hero-picker-section hero-picker-upload-section">
                        <div className="hero-picker-section-title">Upload product hero photo</div>
                        <div className="hero-picker-section-desc">
                          Upload a custom no-background image for the hero. Overrides the product's own photo.
                        </div>
                        <div className="hero-picker-upload-row">
                          {slide.productImage && (
                            <img
                              src={slide.productImage}
                              alt="Hero product preview"
                              className="hero-picker-upload-preview"
                            />
                          )}
                          <div className="hero-picker-upload-fields">
                            <input
                              type="text"
                              className="hero-picker-upload-url"
                              value={slide.productImage}
                              onChange={(e) => updateHeroSlideBg(cat, e.target.value, "productImage")}
                              placeholder="Paste image URL or upload below…"
                            />
                            <div className="hero-picker-upload-btns">
                              <label className="hero-picker-upload-btn">
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => void handleHeroSlideBgUpload(e, cat, "productImage")}
                                />
                                <i className="fa-solid fa-arrow-up-from-bracket" />
                                Upload product hero photo
                              </label>
                              {slide.productImage && (
                                <button
                                  type="button"
                                  className="hero-picker-remove-btn"
                                  onClick={() => updateHeroSlideBg(cat, "", "productImage")}
                                >
                                  <i className="fa-solid fa-trash" />
                                  Remove photo
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </StoreControlPanel>

        <StoreControlPanel
          sectionId="collections"
          isOpen={openSection === "collections"}
          onToggle={toggleStoreSection}
          title="Collections hotspot control"
          description="Per-tile media, uploads, and hotspot products on the storefront."
          badge={`${collectionsTiles.length} tiles`}
        >
          <div className="store-control-panel-inner">
            <div className="store-control-panel-actions">
              <button
                type="button"
                className="store-control-panel-actions-primary"
                onClick={handleSaveCollections}
                disabled={savingCollections || loading}
              >
                {savingCollections ? "Saving…" : "Save collections media"}
              </button>
            </div>
        <div className="store-collections-tiles">
          <h3>All Collection Tiles</h3>
          <p>
            You can switch any tile between image/video/default, upload files, and add multiple
            product hotspots per tile (position each on the preview).
          </p>
          <div className="store-collections-tiles-grid">
            {collectionsTiles.map((tile) => {
              const tileFieldName = `collectionsTile:${tile.id}`;
              const tileMediaUrl = tile.mediaUrl || "";
              const tileIsImage = tile.effectiveMediaType === "image";
              const tileIsVideo = tile.effectiveMediaType === "video";
              return (
                <div className="store-collections-tile-card" key={tile.id}>
                  <div className="store-collections-tile-head">
                    <strong>{tile.label}</strong>
                    <span>ID: {tile.id}</span>
                  </div>
                  <label className="store-collections-field">
                    <span>Media Type</span>
                    <select
                      value={tile.mediaType}
                      onChange={(e) =>
                        updateTileMedia(tile.id, {
                          mediaType: e.target.value,
                        })
                      }
                    >
                      <option value="default">
                        Default ({tile.defaultMediaType === "video" ? "Video" : "Image"})
                      </option>
                      <option value="image">Image</option>
                      <option value="video">Video</option>
                    </select>
                  </label>
                  <input
                    type="text"
                    className="store-collections-tile-url"
                    title={tileMediaUrl}
                    value={tileMediaUrl}
                    placeholder="https://... or /uploads/..."
                    onChange={(e) => updateTileMedia(tile.id, { mediaUrl: e.target.value })}
                  />
                  <div className="store-collections-tile-actions">
                    <label className="store-control-inline-upload-btn">
                      Upload
                      <input
                        type="file"
                        accept="image/*,video/*"
                        onChange={(e) => handlePickUpload(e, tileFieldName)}
                      />
                    </label>
                    <button
                      type="button"
                      className="store-category-crop-edit-btn"
                      onClick={() => handleEditExistingCrop(tileFieldName)}
                      disabled={!tileMediaUrl || tile.effectiveMediaType !== "image"}
                    >
                      Crop
                    </button>
                    <button
                      type="button"
                      className="store-control-delete-btn"
                      onClick={() =>
                        updateTileMedia(tile.id, {
                          mediaType: "default",
                          mediaUrl: "",
                          hotspots: [
                            {
                              localId: newHotspotLocalId(),
                              hotspotX: 50,
                              hotspotY: 50,
                              productId: "",
                            },
                          ],
                          activeHotspotIndex: 0,
                        })
                      }
                    >
                      Reset Media
                    </button>
                  </div>
                  <div
                    className="store-collections-tile-preview"
                    role="button"
                    tabIndex={0}
                    onClick={(e) => handleTilePreviewClick(e, tile.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                      }
                    }}
                  >
                    {tileIsImage ? (
                      <img
                        src={resolveUrl(tile.effectiveMediaUrl, axios.defaults.baseURL)}
                        alt={`${tile.label} preview`}
                        onLoad={(ev) => registerTilePreviewMediaSize(tile.id, ev.currentTarget)}
                      />
                    ) : tileIsVideo ? (
                      <video
                        src={resolveUrl(tile.effectiveMediaUrl, axios.defaults.baseURL)}
                        muted
                        loop
                        autoPlay
                        playsInline
                        onLoadedMetadata={(ev) =>
                          registerTilePreviewMediaSize(tile.id, ev.currentTarget)
                        }
                      />
                    ) : (
                      <span className="store-collections-preview-empty">
                        No media selected.
                      </span>
                    )}
                    {tile.hotspotsList.map((hs, hi) => {
                      const mediaSize = tilePreviewMediaSize[tile.id];
                      const hx = normalizePercent(hs.hotspotX, 50);
                      const hy = normalizePercent(hs.hotspotY, 50);
                      const spotPos = mediaSize
                        ? mediaPercentToContainerCss(
                            hx,
                            hy,
                            TILE_PREVIEW_ASPECT_W,
                            TILE_PREVIEW_ASPECT_H,
                            mediaSize.w,
                            mediaSize.h,
                            "contain"
                          )
                        : { left: `${hx}%`, top: `${hy}%` };
                      return (
                        <span
                          key={hs.localId || `hs-${tile.id}-${hi}`}
                          className={`store-collections-spot${
                            hi === tile.activeHotspotIndex
                              ? " store-collections-spot--active"
                              : ""
                          }`}
                          style={spotPos}
                        />
                      );
                    })}
                  </div>
                  <div className="store-collections-hotspots-editor">
                    <div className="store-collections-hotspots-head">
                      <strong>Hotspots</strong>
                      <button
                        type="button"
                        className="store-collections-add-hotspot-btn"
                        onClick={() => {
                          const saved = collectionsForm.tileMedia?.[tile.id] || {};
                          const list =
                            Array.isArray(saved.hotspots) && saved.hotspots.length > 0
                              ? [...saved.hotspots]
                              : [];
                          const last = list[list.length - 1];
                          const nx = Math.min(92, Math.max(8, Number(last?.hotspotX ?? 50) + 7));
                          const ny = Math.min(92, Math.max(8, Number(last?.hotspotY ?? 50) + 6));
                          updateTileMedia(tile.id, {
                            hotspots: [
                              ...list,
                              {
                                localId: newHotspotLocalId(),
                                hotspotX: nx,
                                hotspotY: ny,
                                productId: "",
                              },
                            ],
                            activeHotspotIndex: list.length,
                          });
                        }}
                      >
                        Add hotspot
                      </button>
                    </div>
                    {tile.hotspotsList.map((hs, hi) => (
                      <div
                        key={hs.localId || `row-${tile.id}-${hi}`}
                        className={`store-collections-hotspot-row${
                          hi === tile.activeHotspotIndex
                            ? " store-collections-hotspot-row--active"
                            : ""
                        }`}
                      >
                        <div className="store-collections-hotspot-row-head">
                          <label className="store-collections-hotspot-select">
                            <input
                              type="radio"
                              name={`hotspot-active-${tile.id}`}
                              checked={hi === tile.activeHotspotIndex}
                              onChange={() =>
                                updateTileMedia(tile.id, { activeHotspotIndex: hi })
                              }
                            />
                            <span>Position on preview</span>
                          </label>
                          {tile.hotspotsList.length > 1 ? (
                            <button
                              type="button"
                              className="store-control-delete-btn store-collections-hotspot-remove"
                              onClick={() => {
                                const saved = collectionsForm.tileMedia?.[tile.id] || {};
                                const list = [...(saved.hotspots || [])];
                                list.splice(hi, 1);
                                if (list.length === 0) {
                                  updateTileMedia(tile.id, {
                                    hotspots: [
                                      {
                                        localId: newHotspotLocalId(),
                                        hotspotX: 50,
                                        hotspotY: 50,
                                        productId: "",
                                      },
                                    ],
                                    activeHotspotIndex: 0,
                                  });
                                  return;
                                }
                                let nextActive = Number(saved.activeHotspotIndex) || 0;
                                if (hi < nextActive) nextActive -= 1;
                                if (nextActive >= list.length) nextActive = list.length - 1;
                                updateTileMedia(tile.id, {
                                  hotspots: list,
                                  activeHotspotIndex: nextActive,
                                });
                              }}
                            >
                              Remove
                            </button>
                          ) : null}
                        </div>
                        <div className="store-collections-controls store-collections-controls--tile store-collections-controls--hotspot">
                          <label className="store-collections-field">
                            <span>Product</span>
                            <select
                              value={hs.productId}
                              onChange={(e) => {
                                const saved = collectionsForm.tileMedia?.[tile.id] || {};
                                const list = [...(saved.hotspots || [])];
                                list[hi] = { ...list[hi], productId: e.target.value };
                                updateTileMedia(tile.id, { hotspots: list });
                              }}
                            >
                              <option value="">None</option>
                              {productOptions.map((product) => (
                                <option key={product._id} value={product._id}>
                                  {product.name} ({product.category})
                                </option>
                              ))}
                            </select>
                          </label>
                          <label className="store-collections-field">
                            <span>X</span>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="0.1"
                              value={formatHotspotCoord(hs.hotspotX, 50)}
                              onChange={(e) => {
                                const saved = collectionsForm.tileMedia?.[tile.id] || {};
                                const list = [...(saved.hotspots || [])];
                                list[hi] = {
                                  ...list[hi],
                                  hotspotX: formatHotspotCoord(
                                    e.target.value,
                                    hs.hotspotX
                                  ),
                                };
                                updateTileMedia(tile.id, { hotspots: list });
                              }}
                            />
                          </label>
                          <label className="store-collections-field">
                            <span>Y</span>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="0.1"
                              value={formatHotspotCoord(hs.hotspotY, 50)}
                              onChange={(e) => {
                                const saved = collectionsForm.tileMedia?.[tile.id] || {};
                                const list = [...(saved.hotspots || [])];
                                list[hi] = {
                                  ...list[hi],
                                  hotspotY: formatHotspotCoord(
                                    e.target.value,
                                    hs.hotspotY
                                  ),
                                };
                                updateTileMedia(tile.id, { hotspots: list });
                              }}
                            />
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="store-collections-hint">
                    Choose “Position on preview” for a hotspot, then click the preview to place it.
                    Media: {tile.effectiveMediaType}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
          </div>
        </StoreControlPanel>

        <StoreControlPanel
          sectionId="categories"
          isOpen={openSection === "categories"}
          onToggle={toggleStoreSection}
          title="Categories control"
          description="Add or edit categories: logo, category page cover, store home cover, and order."
          badge={`${categories.length} categories`}
        >
          <div className="store-control-panel-inner">
        <form className="store-category-form" onSubmit={handleSubmitCategory}>
          <input
            type="text"
            placeholder="Category name (e.g. Office)"
            value={categoryForm.label}
            onChange={(e) =>
              setCategoryForm((prev) => ({ ...prev, label: e.target.value }))
            }
          />
          <input
            type="text"
            placeholder="Arabic name (optional)"
            value={categoryForm.arLabel}
            onChange={(e) =>
              setCategoryForm((prev) => ({ ...prev, arLabel: e.target.value }))
            }
          />
          <input
            type="text"
            placeholder="Slug (auto-generated if empty)"
            value={categoryForm.slug}
            onChange={(e) =>
              setCategoryForm((prev) => ({ ...prev, slug: e.target.value }))
            }
          />
          <label>
            Serial group code (2 letters)
            <input
              type="text"
              maxLength={2}
              value={categoryForm.serialGroupCode}
              onChange={(e) =>
                setCategoryForm((prev) => ({
                  ...prev,
                  serialGroupCode: e.target.value.replace(/[^a-zA-Z]/g, "").toUpperCase(),
                }))
              }
              placeholder="CH"
              style={{ textTransform: "uppercase", letterSpacing: "0.12em", fontFamily: "ui-monospace, monospace" }}
            />
          </label>
          <p className="store-control-field-hint">
            Same code on multiple categories shares one serial sequence (e.g. CH-26-00001).
            Different codes get separate counters.
          </p>
          <select
            value={categoryForm.parentId}
            onChange={(e) => setCategoryForm((prev) => ({ ...prev, parentId: e.target.value }))}
          >
            <option value="">No parent (main category)</option>
            {categoriesFlatWithDepth
              .filter(({ node }) => String(node._id) !== String(editingCategoryId || ""))
              .map(({ node, depth }) => (
                <option key={node._id} value={node._id}>
                  {depth > 0 ? `${"—".repeat(Math.min(6, depth))} ` : ""}
                  {node.label}
                </option>
              ))}
          </select>
          <label className="store-category-form-inline">
            <input
              type="checkbox"
              checked={Boolean(categoryForm.isActive)}
              onChange={(e) =>
                setCategoryForm((prev) => ({ ...prev, isActive: e.target.checked }))
              }
            />
            Active
          </label>
          <input
            type="text"
            className="store-category-form-full"
            placeholder="Category logo URL (e.g. /photos/icon.svg)"
            value={categoryForm.iconUrl}
            onChange={(e) =>
              setCategoryForm((prev) => ({ ...prev, iconUrl: e.target.value }))
            }
          />
          <input
            type="text"
            className="store-category-form-full"
            placeholder="Category page cover image URL"
            value={categoryForm.coverImageUrl}
            onChange={(e) =>
              setCategoryForm((prev) => ({ ...prev, coverImageUrl: e.target.value }))
            }
          />
          <input
            type="text"
            className="store-category-form-full"
            placeholder="Store home “Our Categories” tile image URL"
            value={categoryForm.storeHomeImageUrl}
            onChange={(e) =>
              setCategoryForm((prev) => ({ ...prev, storeHomeImageUrl: e.target.value }))
            }
          />
          <input
            type="text"
            className="store-category-form-full"
            placeholder="SEO title (optional)"
            value={categoryForm.seoTitle}
            onChange={(e) =>
              setCategoryForm((prev) => ({ ...prev, seoTitle: e.target.value }))
            }
          />
          <textarea
            className="store-category-form-full"
            placeholder="SEO description (optional)"
            value={categoryForm.seoDescription}
            onChange={(e) =>
              setCategoryForm((prev) => ({ ...prev, seoDescription: e.target.value }))
            }
            rows={3}
          />
          <div className="store-category-form-actions">
            <button type="submit" disabled={savingCategory}>
              {savingCategory
                ? editingCategoryId
                  ? "Updating..."
                  : "Adding..."
                : editingCategoryId
                ? "Update Category"
                : "Add Category"}
            </button>
            {editingCategoryId ? (
              <button
                type="button"
                className="store-category-cancel-btn"
                onClick={resetCategoryForm}
              >
                Cancel
              </button>
            ) : null}
          </div>
        </form>

        <div className="store-category-upload-grid">
          <label
            className="store-category-dropzone"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => handleDropUpload(e, "iconUrl")}
          >
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handlePickUpload(e, "iconUrl")}
            />
            <span className="store-category-dropzone-title">Category logo</span>
            <span className="store-category-dropzone-text">
              {uploadingField === "iconUrl"
                ? "Uploading..."
                : "Drag & drop logo or click to upload"}
            </span>
            {categoryForm.iconUrl ? (
              <img src={categoryForm.iconUrl} alt="" className="store-category-drop-preview store-category-drop-preview--icon" />
            ) : null}
          </label>

          <label
            className="store-category-dropzone"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => handleDropUpload(e, "coverImageUrl")}
          >
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handlePickUpload(e, "coverImageUrl")}
            />
            <span className="store-category-dropzone-title">Category page cover</span>
            <span className="store-category-dropzone-text">
              {uploadingField === "coverImageUrl"
                ? "Uploading..."
                : "Used on the category listing page header"}
            </span>
            {categoryForm.coverImageUrl ? (
              <img src={categoryForm.coverImageUrl} alt="" className="store-category-drop-preview" />
            ) : null}
          </label>

          <label
            className="store-category-dropzone"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => handleDropUpload(e, "storeHomeImageUrl")}
          >
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handlePickUpload(e, "storeHomeImageUrl")}
            />
            <span className="store-category-dropzone-title">Store home cover</span>
            <span className="store-category-dropzone-text">
              {uploadingField === "storeHomeImageUrl"
                ? "Uploading..."
                : "Used on store home “Our Categories” tiles"}
            </span>
            {categoryForm.storeHomeImageUrl ? (
              <img src={categoryForm.storeHomeImageUrl} alt="" className="store-category-drop-preview" />
            ) : null}
          </label>
        </div>
        <div className="store-category-upload-actions">
          <button
            type="button"
            className="store-category-crop-edit-btn"
            onClick={() => handleEditExistingCrop("iconUrl")}
            disabled={!categoryForm.iconUrl || uploadingField === "iconUrl"}
          >
            Crop logo
          </button>
          <button
            type="button"
            className="store-category-crop-edit-btn"
            onClick={() => handleEditExistingCrop("coverImageUrl")}
            disabled={!categoryForm.coverImageUrl || uploadingField === "coverImageUrl"}
          >
            Crop category cover
          </button>
          <button
            type="button"
            className="store-category-crop-edit-btn"
            onClick={() => handleEditExistingCrop("storeHomeImageUrl")}
            disabled={
              !categoryForm.storeHomeImageUrl || uploadingField === "storeHomeImageUrl"
            }
          >
            Crop store home image
          </button>
        </div>

        <div className="store-control-table store-control-table--category-tree">
          <div className="store-control-row store-control-head store-control-row--categories-tree">
            <span>Category</span>
            <span>Code</span>
            <span>Status</span>
            <span>Edit</span>
            <span>Action</span>
          </div>
          {categoriesFlatWithDepth.map(({ node: category, depth }) => {
            const id = String(category._id);
            const hasChildren = Array.isArray(category.children) && category.children.length > 0;
            const isOpen = adminCatsOpen.has(id);
            const isInactive = category.isActive === false;

            // Hide when any ancestor is collapsed
            if (depth > 0) {
              let cur = category;
              let guard = 0;
              let visible = true;
              while (cur?.parentId && guard < 30) {
                const pid = String(cur.parentId);
                if (!adminCatsOpen.has(pid)) {
                  visible = false;
                  break;
                }
                cur = categories.find((c) => String(c._id) === pid);
                guard += 1;
              }
              if (!visible) return null;
            }

            return (
              <div
                className={`store-control-row store-control-row--categories-tree${isInactive ? " is-inactive" : ""}`}
                key={id}
              >
                <span className="store-control-cat-tree-cell">
                  <button
                    type="button"
                    className={`store-control-cat-expander${hasChildren ? "" : " is-empty"}${
                      isOpen ? " is-open" : ""
                    }`}
                    onClick={() => (hasChildren ? toggleAdminCatOpen(id) : undefined)}
                    aria-label={isOpen ? "Collapse" : "Expand"}
                    disabled={!hasChildren}
                    style={{ marginInlineStart: `${Math.max(0, depth) * 16}px` }}
                  >
                    <i className="fa-solid fa-chevron-right" aria-hidden="true" />
                  </button>
                  <span className="store-control-cat-name">{category.label}</span>
                </span>
                <span className="store-control-cat-code">
                  {String(category.serialGroupCode || "").toUpperCase() || "—"}
                </span>
                <span>
                  <button
                    type="button"
                    className={`store-control-status-btn${isInactive ? " is-off" : " is-on"}`}
                    onClick={() => toggleCategoryActive(category)}
                  >
                    {isInactive ? "Inactive" : "Active"}
                  </button>
                </span>
                <span>
                  <button
                    type="button"
                    className="store-control-edit-btn"
                    onClick={() => startEditCategory(category)}
                  >
                    Edit
                  </button>
                </span>
                <span>
                  <button
                    type="button"
                    className="store-control-delete-btn"
                    onClick={() => handleDeleteCategory(category._id)}
                  >
                    Remove
                  </button>
                </span>
              </div>
            );
          })}
        </div>
          </div>
        </StoreControlPanel>
      </div>
      {cropModal.open ? (
        <div className="store-category-crop-modal" onClick={closeCropModal}>
          <div
            className="store-category-crop-content"
            onClick={(e) => e.stopPropagation()}
          >
            <h4>
              Crop{" "}
              {cropModal.fieldName === "heroProductImage"
                ? "hero product image (4:5 for storefront hero)"
                : cropModal.fieldName === "iconUrl"
                ? "category logo"
                : cropModal.fieldName === "coverImageUrl"
                ? "category page cover"
                : cropModal.fieldName === "storeHomeImageUrl"
                ? "store home cover"
                : cropModal.fieldName === "collectionsPhotoUrl"
                ? "Collections Photo"
                : cropModal.fieldName.startsWith("collectionsTile:")
                ? "Collection Tile Photo"
                : "image"}
            </h4>
            <div className="store-category-crop-area">
              <ReactCrop
                crop={crop}
                onChange={(_, percentCrop) => setCrop(percentCrop)}
                onComplete={(pixelCrop) => setCompletedCrop(pixelCrop)}
                aspect={cropAspect}
              >
                <img
                  ref={setImgRef}
                  src={cropModal.src}
                  alt="Crop preview"
                  onLoad={onCropImageLoad}
                  className={
                    cropModal.fieldName === "iconUrl"
                      ? "store-category-crop-image store-category-crop-image--icon"
                      : "store-category-crop-image"
                  }
                />
              </ReactCrop>
            </div>
            <div className="store-category-crop-actions">
              <button type="button" onClick={closeCropModal}>
                Cancel
              </button>
              <button
                type="button"
                onClick={handleApplyCrop}
                disabled={!completedCrop || uploadingField === cropModal.fieldName}
              >
                {uploadingField === cropModal.fieldName ? "Uploading..." : "Apply Crop"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </main>
  );
}
